<template>
  <section class="task-board-page h-full w-full flex flex-col bg-gray-50 min-h-screen">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <!-- Breadcrumb -->
      <nav class="text-sm text-gray-600 mb-6 flex flex-wrap items-center gap-x-1">
        <router-link to="/dashboard" class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
        <span class="mx-1">/</span>
        <router-link to="/tasks" class="hover:text-blue-700 transition font-medium">Task Management</router-link>
        <span class="mx-1">/</span>
        <router-link v-if="boardData && boardData.master_board" :to="'/tasks/master-board/' + boardData.master_board.id"
          class="hover:text-blue-700 transition font-medium">{{ boardData.master_board.name }}</router-link>
        <template v-if="boardData && boardData.master_board">
          <span class="mx-1">/</span>
        </template>
        <span v-if="boardData" class="text-blue-900 font-semibold">{{ boardData.name }}</span>
        <span v-else class="text-blue-900 font-semibold">Loading...</span>
      </nav>

      <!-- Header -->
      <div class="bg-white border border-gray-200 rounded-3xl shadow-md p-8 mb-6 sm:mb-8">
        <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div class="min-w-0">
            <h1 class="text-3xl font-bold text-blue-900 flex items-center gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
                <path
                  d="M7.5 3.75A2.25 2.25 0 0 0 5.25 6v12A2.25 2.25 0 0 0 7.5 20.25h9A2.25 2.25 0 0 0 18.75 18V7.5a.75.75 0 0 0-.22-.53l-3-3a.75.75 0 0 0-.53-.22H7.5Zm3 6.75a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Zm0 3a.75.75 0 0 1 .75-.75h4.5a.75.75 0 0 1 0 1.5h-4.5a.75.75 0 0 1-.75-.75Zm-3-3a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 0 1.5H8.25a.75.75 0 0 1-.75-.75Zm0 3a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 0 1.5H8.25a.75.75 0 0 1-.75-.75Z" />
              </svg>
              <span v-if="boardData">{{ boardData.name }}</span>
              <span v-else class="h-8 bg-gray-200 rounded w-48 animate-pulse"></span>
              <div v-if="boardLoading" class="ml-2">
                <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            </h1>
            <p v-if="boardData && boardData.description" class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
              {{ boardData.description }}
            </p>
            <p v-else-if="boardData && !boardData.description"
              class="text-gray-600 mt-2 text-base leading-relaxed max-w-3xl">
              Manage and track tasks across different stages of completion.
            </p>
            <div v-else class="mt-2 h-4 bg-gray-200 rounded w-96 animate-pulse"></div>
          </div>

          <div class="flex items-center justify-start lg:justify-end gap-3">
            <button type="button" @click="openExportModal" :disabled="!boardData"
              class="px-6 py-3 text-primary border border-primary rounded-lg hover:bg-primary hover:text-white transition-colors duration-200 flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
              <font-awesome-icon :icon="['fas', 'file-excel']" class="w-5 h-5" />
              <span>Export Excel</span>
            </button>
            <button @click="showCreateTaskModal = true"
              class="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 cursor-pointer">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span>New Task</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Board Members Section -->
      <div v-if="boardData && (boardMembersLoading || boardMembers.length > 0)" class="mt-2 mb-6">
        <div class="bg-white rounded-2xl border shadow-lg p-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                </path>
              </svg>
              <h2 class="text-lg font-semibold text-gray-900">Sub-Board Members</h2>
              <span class="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                {{ boardMembersLoading ? '...' : boardMembers.length }} {{ boardMembers.length === 1 ? 'member' :
                  'members' }}
              </span>
            </div>
          </div>
          <div v-if="boardMembersLoading && boardMembers.length === 0" class="py-12 text-center">
            <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-500 mx-auto"></div>
            <p class="text-sm text-gray-500 mt-2">Loading members...</p>
          </div>
          <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            <div v-for="member in boardMembers" :key="member.id" @click="openMemberTasksModal(member)"
              class="member-card flex flex-col overflow-hidden bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-xl hover:border-blue-300/60 transition-all duration-300 cursor-pointer group">
              <div
                class="h-1 w-full bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                :style="boardData && boardData.color ? { background: `linear-gradient(90deg, ${boardData.color}, ${boardData.color}dd)` } : {}">
              </div>
              <div class="p-5 flex flex-col flex-1">
                <div class="flex items-start gap-4">
                  <!-- Avatar -->
                  <div class="relative flex-shrink-0">
                    <div
                      class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden ring-2 ring-white shadow-md group-hover:ring-blue-100 transition-all duration-300 cursor-pointer">
                      <img @click.stop="goToProfile(member)" :src="getProfilePicture(member.profile_picture_url)"
                        :alt="member.name" class="w-full h-full object-cover" @error="handleImageError" />
                    </div>
                    <div v-if="isCreator(member)"
                      class="absolute -top-1 -right-1 w-6 h-6 bg-blue-600 rounded-lg flex items-center justify-center border-2 border-white shadow-sm"
                      title="Board Creator">
                      <svg class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd"
                          d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clip-rule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <!-- Member Info -->
                  <div class="flex-1 min-w-0 pt-0.5 flex flex-col">
                    <h3
                      class="text-sm font-semibold text-gray-900 truncate transition-colors cursor-pointer group-hover:text-blue-600 hover:text-blue-600 hover:underline block w-full">
                      {{ member.name }}
                    </h3>
                    <p
                      class="text-xs text-gray-500 truncate mt-0.5 cursor-pointer hover:text-blue-600 hover:underline block w-full">
                      {{ member.email }}
                    </p>
                  </div>
                </div>
                <!-- Task Stats -->
                <div class="mt-4 pt-4 border-t border-gray-100">
                  <div class="flex flex-wrap gap-2">
                    <div class="stat-pill stat-total">
                      <span class="stat-label">Total</span>
                      <span class="stat-value">{{ getMemberTaskStats(member).total }}</span>
                    </div>
                    <div class="stat-pill stat-todo" :class="{ 'stat-empty': getMemberTaskStats(member).todo === 0 }">
                      <span class="stat-label">To Do</span>
                      <span class="stat-value">{{ getMemberTaskStats(member).todo }}</span>
                    </div>
                    <div class="stat-pill stat-progress"
                      :class="{ 'stat-empty': getMemberTaskStats(member).in_progress === 0 }">
                      <span class="stat-label">In Progress</span>
                      <span class="stat-value">{{ getMemberTaskStats(member).in_progress }}</span>
                    </div>
                    <div class="stat-pill stat-paused"
                      :class="{ 'stat-empty': getMemberTaskStats(member).paused === 0 }">
                      <span class="stat-label">Paused</span>
                      <span class="stat-value">{{ getMemberTaskStats(member).paused }}</span>
                    </div>
                    <div class="stat-pill stat-review"
                      :class="{ 'stat-empty': getMemberTaskStats(member).in_review === 0 }">
                      <span class="stat-label">In Review</span>
                      <span class="stat-value">{{ getMemberTaskStats(member).in_review }}</span>
                    </div>
                    <div class="stat-pill stat-done" :class="{ 'stat-empty': getMemberTaskStats(member).done === 0 }">
                      <span class="stat-label">Done</span>
                      <span class="stat-value">{{ getMemberTaskStats(member).done }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Attachments Archive Section -->
      <div v-if="boardData" class="mt-2 mb-6">
        <div class="bg-white rounded-2xl border shadow-lg p-6">
          <div class="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div class="flex items-center gap-3">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L21 13" />
              </svg>
              <h2 class="text-lg font-semibold text-gray-900">Attachments Archive</h2>
              <span class="text-sm text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                {{ attachments.length }} {{ attachments.length === 1 ? 'file' : 'files' }}
              </span>
            </div>
            <div v-if="canUploadToBoard" class="flex items-center gap-2 flex-wrap">
              <button type="button" @click="createAttachmentFolder"
                class="px-4 py-2 rounded-lg text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors flex items-center gap-2">
                <icon-folder-plus class="w-4 h-4" />
                New Folder
              </button>
              <label
                class="px-4 py-2 rounded-lg text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-white transition-colors cursor-pointer flex items-center gap-2">
                <font-awesome-icon icon="upload" class="h-4 w-4" />
                Upload
                <input type="file" ref="boardAttachmentInput" class="hidden"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  @change="onBoardAttachmentSelected" />
              </label>
            </div>
          </div>

            <div class="archive-explorer" @contextmenu.prevent="openArchiveExplorerContextMenu($event)">
              <nav class="archive-address-bar">
                <button type="button" @click="navigateAttachmentFolder(null)" class="archive-address-root"
                  title="Attachments Archive">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span>Attachments Archive</span>
                </button>
                <template v-for="(crumb, crumbIndex) in attachmentBreadcrumb" :key="crumb.id">
                  <span class="archive-address-sep">/</span>
                  <button v-if="crumbIndex < attachmentBreadcrumb.length - 1" type="button"
                    @click="navigateAttachmentFolder(crumb.id)" class="archive-address-crumb">
                    {{ crumb.name }}
                  </button>
                  <span v-else class="archive-address-current">{{ crumb.name }}</span>
                </template>
              </nav>

              <div v-if="boardUploading || boardFolderCreating || archivePasting" class="archive-status">
                <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-500 mx-auto"></div>
                <p class="text-sm mt-2 text-gray-500">{{ archivePastingLabel }}</p>
              </div>
              <div v-else-if="!hasCurrentArchiveItems" class="archive-empty"
                @contextmenu.prevent="openArchiveBackgroundContextMenu($event)">
                <svg class="archive-empty-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <p class="text-sm text-gray-500">This folder is empty</p>
                <p class="text-xs text-gray-400 mt-1">Add a folder or upload files to get started</p>
              </div>
              <div v-else class="archive-grid" ref="archiveGrid"
                @mousedown="onArchiveGridMouseDown"
                @contextmenu.prevent="openArchiveExplorerContextMenu($event)">
                <div v-for="folder in currentFolders" :key="'folder-' + folder.id" class="archive-grid-item"
                  data-archive-type="folder"
                  :data-archive-id="folder.id"
                  :class="{
                  'archive-grid-item-selected': isArchiveItemSelected('folder', folder.id),
                  'archive-grid-item-cut': isArchiveItemCut('folder', folder.id),
                }" @click="onArchiveGridItemClick($event, 'folder', folder.id)" @dblclick="openAttachmentFolder(folder)"
                  @contextmenu.prevent="openArchiveContextMenu($event, 'folder', folder)">
                  <button type="button" class="archive-item-kebab-menu"
                    :class="{ 'archive-item-kebab-menu-active': isArchiveContextMenuOpenForItem('folder', folder.id) }"
                    title="More options"
                    @click.stop="openArchiveItemKebabMenu($event, 'folder', folder)">
                    <font-awesome-icon icon="ellipsis-v" class="archive-kebab-icon" />
                  </button>
                  <div class="archive-item-icon-wrap">
                    <img :src="macFolderIcon" alt="" class="archive-folder-icon" />
                  </div>
                  <div class="archive-item-label-wrap">
                    <input v-if="inlineRenamingFolderId && String(inlineRenamingFolderId) === String(folder.id)"
                      :ref="'folderRenameInput-' + folder.id" v-model="inlineRenamingFolderName" type="text"
                      class="archive-item-label-input" @click.stop @dblclick.stop
                      @keydown.enter.prevent="commitInlineRenameAttachmentFolder(folder)"
                      @keydown.esc.prevent="cancelInlineRenameAttachmentFolder"
                      @blur="commitInlineRenameAttachmentFolder(folder)" />
                    <p v-else class="archive-item-label" :title="folder.name"
                      @click.stop="onArchiveLabelClick($event, 'folder', folder)">{{
                        folder.name }}</p>
                  </div>
                  <div v-if="!(inlineRenamingFolderId && String(inlineRenamingFolderId) === String(folder.id))"
                    class="archive-meta-tooltip">
                    <p class="archive-meta-title">{{ folder.name }}</p>
                    <div class="archive-meta-rows">
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Type</span>
                        <span class="archive-meta-value">Folder</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Contains</span>
                        <span class="archive-meta-value">{{ getFolderContentsLabel(folder.id) }}</span>
                      </div>
                      <div v-if="folder.created_by && folder.created_by.name" class="archive-meta-row">
                        <span class="archive-meta-label">Created by</span>
                        <span class="archive-meta-value">{{ folder.created_by.name }}</span>
                      </div>
                      <div v-if="folder.created_at" class="archive-meta-row">
                        <span class="archive-meta-label">Created</span>
                        <span class="archive-meta-value">{{ formatArchiveDateTime(folder.created_at) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-for="att in currentAttachments" :key="'att-' + att.id" class="archive-grid-item"
                  data-archive-type="file"
                  :data-archive-id="att.id"
                  :class="{
                  'archive-grid-item-selected': isArchiveItemSelected('file', att.id),
                  'archive-grid-item-cut': isArchiveItemCut('file', att.id),
                }" @click="onArchiveGridItemClick($event, 'file', att.id)" @dblclick="viewAttachment(att)"
                  @contextmenu.prevent="openArchiveContextMenu($event, 'file', att)">
                  <button type="button" class="archive-item-kebab-menu"
                    :class="{ 'archive-item-kebab-menu-active': isArchiveContextMenuOpenForItem('file', att.id) }"
                    title="More options"
                    @click.stop="openArchiveItemKebabMenu($event, 'file', att)">
                    <font-awesome-icon icon="ellipsis-v" class="archive-kebab-icon" />
                  </button>
                  <div class="archive-item-icon-wrap">
                    <div class="archive-file-icon" :class="getArchiveFileIconClass(att)">
                      <svg class="archive-file-icon-svg" viewBox="0 0 48 56" fill="none">
                        <path
                          d="M8 4C8 2.89543 8.89543 2 10 2H26.5L40 15.5V52C40 53.1046 39.1046 54 38 54H10C8.89543 54 8 53.1046 8 52V4Z"
                          fill="currentColor" fill-opacity="0.15" stroke="currentColor" stroke-width="2" />
                        <path d="M26 2V14C26 15.1046 26.8954 16 28 16H40" stroke="currentColor" stroke-width="2" />
                      </svg>
                      <span class="archive-file-ext">{{ getArchiveFileExtension(att) }}</span>
                    </div>
                  </div>
                  <div class="archive-item-label-wrap">
                    <input v-if="inlineRenamingAttachmentId && String(inlineRenamingAttachmentId) === String(att.id)"
                      :ref="'attachmentRenameInput-' + att.id" v-model="inlineRenamingAttachmentName" type="text"
                      class="archive-item-label-input" @click.stop @dblclick.stop
                      @keydown.enter.prevent="commitInlineRenameAttachment(att)"
                      @keydown.esc.prevent="cancelInlineRenameAttachment" @blur="commitInlineRenameAttachment(att)" />
                    <p v-else class="archive-item-label" :title="getAttachmentDisplayName(att)"
                      @click.stop="onArchiveLabelClick($event, 'file', att)">{{ getAttachmentDisplayName(att) }}</p>
                  </div>
                  <div v-if="!(inlineRenamingAttachmentId && String(inlineRenamingAttachmentId) === String(att.id))"
                    class="archive-meta-tooltip">
                    <p class="archive-meta-title">{{ getAttachmentDisplayName(att) }}</p>
                    <div class="archive-meta-rows">
                      <div v-if="getAttachmentOriginalName(att) !== getAttachmentDisplayName(att)"
                        class="archive-meta-row">
                        <span class="archive-meta-label">Original</span>
                        <span class="archive-meta-value">{{ getAttachmentOriginalName(att) }}</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Type</span>
                        <span class="archive-meta-value">{{ formatArchiveMimeType(att.file_type) }}</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Extension</span>
                        <span class="archive-meta-value">{{ getArchiveFileExtension(att) }}</span>
                      </div>
                      <div class="archive-meta-row">
                        <span class="archive-meta-label">Size</span>
                        <span class="archive-meta-value">{{ formatArchiveFileSize(att.file_size) }}</span>
                      </div>
                      <div v-if="att.uploaded_by && att.uploaded_by.name" class="archive-meta-row">
                        <span class="archive-meta-label">Uploaded by</span>
                        <span class="archive-meta-value">{{ att.uploaded_by.name }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="archiveMarquee.active" class="archive-marquee" :style="archiveMarqueeStyle"></div>
              </div>

              <div v-if="archiveContextMenu.visible" class="archive-context-menu"
                :style="{ left: archiveContextMenu.x + 'px', top: archiveContextMenu.y + 'px' }" @click.stop
                @contextmenu.prevent>
                <button v-for="action in archiveContextMenuActions" :key="action.id" type="button"
                  class="archive-context-menu-item" :class="{ 'archive-context-menu-item-danger': action.danger }"
                  @click="executeArchiveContextMenuAction(action.id)">
                  <span class="archive-context-menu-icon">
                    <svg v-if="action.id === 'open'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    <svg v-else-if="action.id === 'download'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <svg v-else-if="action.id === 'copy'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                    </svg>
                    <icon-cut v-else-if="action.id === 'cut'" class="archive-context-menu-icon-svg" />
                    <icon-folder-plus v-else-if="action.id === 'new-folder'" class="archive-context-menu-icon-svg" />
                    <svg v-else-if="action.id === 'paste'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <svg v-else-if="action.id === 'rename'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <svg v-else-if="action.id === 'delete'" class="archive-context-menu-icon-svg" fill="none"
                      stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </span>
                  <span class="archive-context-menu-label">{{ action.label }}</span>
                </button>
              </div>
            </div>

        </div>
      </div>

      <!-- Tabs -->
      <div class="mt-2 mb-6">
        <div class="bg-white rounded-2xl border shadow-lg">
          <div class="flex border-b">
            <button @click="setActiveTab('my')" :class="[
              'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-l-2xl',
              activeTab === 'my'
                ? 'text-blue-700 border-b-2 border-blue-600 bg-gray-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            ]">
              <div class="flex items-center justify-center">
                My Tasks
              </div>
            </button>
            <button @click="setActiveTab('all')" :class="[
              'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200',
              activeTab === 'all'
                ? 'text-blue-700 border-b-2 border-blue-600 bg-gray-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            ]">
              <div class="flex items-center justify-center">
                All Tasks
              </div>
            </button>
            <button @click="setActiveTab('viewer')" :class="[
              'flex-1 px-6 py-4 text-sm font-medium transition-colors duration-200 rounded-r-2xl',
              activeTab === 'viewer'
                ? 'text-blue-700 border-b-2 border-blue-600 bg-gray-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            ]">
              <div class="flex items-center justify-center">
                Viewer
              </div>
            </button>
          </div>
        </div>
      </div>

      <!-- Statistics Section -->
      <div v-if="statisticsData || statisticsLoading" class="mb-6">
        <!-- Loading State -->
        <div v-if="statisticsLoading" class="space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div v-for="i in 5" :key="i" class="bg-gray-100 rounded-lg p-4 animate-pulse">
              <div class="h-4 bg-gray-300 rounded w-20 mb-2"></div>
              <div class="h-8 bg-gray-300 rounded w-16"></div>
            </div>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div class="bg-gray-100 rounded-lg p-6 h-80 animate-pulse"></div>
            <div class="bg-gray-100 rounded-lg p-6 h-80 animate-pulse"></div>
          </div>
        </div>

        <!-- Statistics Content -->
        <div v-else-if="statisticsData">
          <!-- Statistics Tiles -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
            <div class="stat-tile stat-tile-total bg-white rounded-lg border border-gray-200 p-4">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Total Tasks</div>
              <div class="text-2xl font-semibold text-gray-900">{{ statisticsData.total }}</div>
            </div>
            <div class="stat-tile stat-tile-todo bg-white rounded-lg border border-gray-200 p-4">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">To Do</div>
              <div class="text-2xl font-semibold text-gray-900">{{ statisticsData.todo }}</div>
            </div>
            <div class="stat-tile stat-tile-progress bg-white rounded-lg border border-gray-200 p-4">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">In Progress</div>
              <div class="text-2xl font-semibold text-gray-900">{{ statisticsData.in_progress }}</div>
            </div>
            <div class="stat-tile stat-tile-paused bg-white rounded-lg border border-gray-200 p-4">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Paused</div>
              <div class="text-2xl font-semibold text-gray-900">{{ statisticsData.paused }}</div>
            </div>
            <div class="stat-tile stat-tile-review bg-white rounded-lg border border-gray-200 p-4">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">In Review</div>
              <div class="text-2xl font-semibold text-gray-900">{{ statisticsData.in_review }}</div>
            </div>
            <div class="stat-tile stat-tile-done bg-white rounded-lg border border-gray-200 p-4">
              <div class="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Done</div>
              <div class="text-2xl font-semibold text-gray-900">{{ statisticsData.done }}</div>
            </div>
          </div>

          <!-- Charts -->
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Board Members Tasks Chart (All Tasks and Viewer tabs) -->
            <div v-if="(activeTab === 'all' || activeTab === 'viewer') && boardMembers.length > 0"
              class="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6 shadow-md">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Tasks by Sub-Board Member</h3>
              <apexchart type="bar" height="300" :options="memberChartOptions" :series="memberChartSeries"
                @dataPointSelection="handleBarClick" />
            </div>

            <!-- Bucket Chart -->
            <div class="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Tasks by Bucket</h3>
              <apexchart v-if="statisticsData.bucket_stats && statisticsData.bucket_stats.length > 0" type="bar"
                height="300" :options="bucketChartOptions" :series="bucketChartSeries" />
              <p v-else class="text-sm text-gray-500 py-8 text-center">No buckets configured</p>
            </div>

            <!-- Status Chart -->
            <div class="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Tasks by Status</h3>
              <apexchart v-if="statisticsData.status_stats" type="bar" height="300" :options="statusChartOptions"
                :series="statusChartSeries" />
            </div>

            <!-- Priority Chart -->
            <div class="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
              <h3 class="text-lg font-semibold text-gray-900 mb-4">Tasks by Priority</h3>
              <apexchart v-if="statisticsData.priority_stats" type="donut" height="300" :options="priorityChartOptions"
                :series="priorityChartSeries" />
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="boardLoading" class="flex gap-4 overflow-x-auto pb-4">
        <div v-for="i in 4" :key="i" class="flex-shrink-0 w-80 bg-white rounded-lg shadow-md p-4 animate-pulse">
          <div class="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div class="space-y-3">
            <div v-for="j in 3" :key="j" class="h-24 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>

      <!-- Tasks: Board (Kanban) or Grid (table) -->
      <div v-else-if="boardData && columns">
        <div class="w-full mb-5">
          <div
            class="board-task-search-panel w-full bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-4 sm:px-6 sm:py-5">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div class="flex-1 min-w-0">
                <label for="board-task-search"
                  class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Search tasks</label>
                <div class="relative">
                  <svg class="absolute left-4 top-3 w-5 h-5 text-gray-400 pointer-events-none" fill="none"
                    stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                  </svg>
                  <input id="board-task-search" v-model="boardTaskSearch" type="search"
                    class="board-task-search-input w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 text-base"
                    placeholder="Search by task title or description" autocomplete="off" />
                </div>
              </div>
              <div class="board-view-toggle flex-shrink-0">
                <span class="block text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 text-center lg:text-left">View</span>
                <div class="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 shadow-inner" role="group" aria-label="Task view mode">
                  <button type="button" class="board-view-toggle-btn" :class="{ 'board-view-toggle-btn-active': taskViewMode === 'grid' }"
                    @click="setTaskViewMode('grid')" title="Grid view">
                    <font-awesome-icon icon="th" class="board-view-toggle-icon" />
                    <span>Grid</span>
                  </button>
                  <button type="button" class="board-view-toggle-btn" :class="{ 'board-view-toggle-btn-active': taskViewMode === 'board' }"
                    @click="setTaskViewMode('board')" title="Board view">
                    <font-awesome-icon :icon="['fas', 'columns']" class="board-view-toggle-icon" />
                    <span>Board</span>
                  </button>
                  <button type="button" class="board-view-toggle-btn" :class="{ 'board-view-toggle-btn-active': taskViewMode === 'calendar' }"
                    @click="setTaskViewMode('calendar')" title="Calendar view">
                    <font-awesome-icon icon="calendar-days" class="board-view-toggle-icon" />
                    <span>Calendar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Grid view (table) -->
        <div v-if="taskViewMode === 'grid'" ref="boardListGridAnchor" class="board-list-view">
          <div class="board-list-table-card bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div v-if="listTasksLoading && listTasks.length === 0" class="flex flex-col items-center justify-center py-20">
              <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-600"></div>
              <p class="text-sm text-gray-500 mt-3">Loading tasks...</p>
            </div>
            <div v-else-if="!listTasksLoading && listTasks.length === 0" class="text-center py-16 px-4">
              <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p class="text-sm text-gray-600">No tasks found.</p>
              <p v-if="boardTaskSearch" class="text-xs text-gray-400 mt-1">Try adjusting your search.</p>
            </div>
            <div v-else class="overflow-x-auto board-list-table-wrap" :class="{ 'board-list-table-wrap--loading': listTasksLoading && listTasks.length > 0 }">
              <table class="board-list-table w-full min-w-[960px]">
                <thead>
                  <tr>
                    <th class="board-list-th board-list-th-num">#</th>
                    <th class="board-list-th">Task</th>
                    <th class="board-list-th">Bucket</th>
                    <th class="board-list-th">Priority</th>
                    <th class="board-list-th">Status</th>
                    <th class="board-list-th">Assignees</th>
                    <th class="board-list-th">Created by</th>
                    <th class="board-list-th">Created</th>
                    <th class="board-list-th">Due</th>
                    <th class="board-list-th board-list-th-action"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(task, index) in listTasks" :key="task.id" class="board-list-row" @click="handleTaskClick(task.id)">
                    <td class="board-list-td board-list-td-num">{{ listTaskNumber(index) }}</td>
                    <td class="board-list-td">
                      <div class="board-list-title">{{ task.title }}</div>
                      <p v-if="task.description" class="board-list-desc">{{ task.description }}</p>
                    </td>
                    <td class="board-list-td">
                      <span v-if="task.column && task.column.name" class="board-list-bucket">
                        <span class="board-list-bucket-dot" :style="{ backgroundColor: getBucketColor(task.column) }"></span>
                        {{ task.column.name }}
                      </span>
                      <span v-else class="text-gray-400">—</span>
                    </td>
                    <td class="board-list-td">
                      <span class="board-list-priority" :style="{ color: getPriorityColor(task.priority), borderColor: getPriorityColor(task.priority) }">
                        {{ formatPriorityLabel(task.priority) }}
                      </span>
                    </td>
                    <td class="board-list-td">
                      <span :class="[getStatusBadgeClass(task.status), 'board-list-status']">{{ getStatusLabel(task.status) }}</span>
                    </td>
                    <td class="board-list-td board-list-td-muted">{{ formatAssigneesList(task.assignees) }}</td>
                    <td class="board-list-td board-list-td-muted">{{ task.created_by && task.created_by.name ? task.created_by.name : '—' }}</td>
                    <td class="board-list-td board-list-td-muted">{{ formatCreatedAt(task.created_at) }}</td>
                    <td class="board-list-td" :class="{ 'board-list-due-late': isTaskDueLate(task) }">{{ formatDueDateDisplay(task.due_date) }}</td>
                    <td class="board-list-td board-list-td-action">
                      <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-if="listPaginatorInfo && listPaginatorInfo.total > 0" class="board-list-pagination mt-4">
            <master-board-modal-pagination :paginator-info="listPaginatorInfo" :loading="listTasksLoading && listTasks.length > 0"
              accent-color="#2563eb" @page-change="setListTasksPage" />
          </div>
        </div>

        <!-- Calendar view -->
        <board-calendar-view
          v-else-if="taskViewMode === 'calendar'"
          :tasks="calendarTasks"
          :loading="calendarTasksLoading"
          :can-add-task="canUploadToBoard"
          @task-click="handleTaskClick"
          @add-task="showCreateTaskModal = true"
        />

        <!-- Board view (Kanban buckets) -->
        <div v-else class="flex gap-4 overflow-x-auto pb-4" style="min-height: 500px;">
          <div v-for="column in localColumns" :key="column.id"
            class="kanban-column flex-shrink-0 w-80 bg-white rounded-lg border border-gray-200 p-4 flex flex-col shadow-sm">
            <!-- Column Header -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <div class="w-3 h-3 rounded-full flex-shrink-0" :style="{ backgroundColor: column.color || '#9BA4B5' }">
                </div>
                <template v-if="editingColumnId === column.id">
                  <div class="flex flex-col flex-1 min-w-0">
                    <div class="flex items-center gap-1">
                      <input ref="editBucketInput" v-model="editBucketName" type="text"
                        class="flex-1 min-w-0 px-2 py-1 text-sm font-semibold border border-blue-300 rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                        :disabled="editBucketLoading" @keydown.enter="saveBucketName(column)"
                        @keydown.esc="cancelEditBucket" />
                      <button @click="saveBucketName(column)" :disabled="editBucketLoading || !editBucketName.trim()"
                        class="p-1 rounded hover:bg-green-100 text-green-600 flex-shrink-0" title="Save">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7">
                          </path>
                        </svg>
                      </button>
                      <button @click="cancelEditBucket" :disabled="editBucketLoading"
                        class="p-1 rounded hover:bg-gray-100 text-gray-600 flex-shrink-0" title="Cancel">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12">
                          </path>
                        </svg>
                      </button>
                    </div>
                    <p v-if="editBucketError" class="text-xs text-red-600 mt-1">{{ editBucketError }}</p>
                  </div>
                </template>
                <template v-else>
                  <h3 class="font-semibold text-gray-900 truncate flex-1 min-w-0">{{ column.name }}</h3>
                  <button v-if="canUploadToBoard" @click="startEditBucket(column)"
                    class="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 flex-shrink-0"
                    title="Edit bucket name">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z">
                      </path>
                    </svg>
                  </button>
                </template>
                <span class="text-sm text-gray-500 bg-white px-2 py-1 rounded-full flex-shrink-0">
                  {{ (column.tasks && column.tasks.length) || 0 }}{{ getColumnTotal(column) > 0 ? ' of ' +
                    getColumnTotal(column) : '' }}
                </span>
                <button v-if="canUploadToBoard && editingColumnId !== column.id" @click="openQuickAddTask(column)"
                  class="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-primary flex-shrink-0"
                  title="Add task">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                </button>
              </div>
            </div>

            <!-- Tasks in Column -->
            <draggable :list="column.tasks" item-key="id" :group="{ name: 'tasks', pull: true, put: true }"
              :animation="180" :force-fallback="true" :empty-insert-threshold="32" ghost-class="task-ghost"
              drag-class="task-drag" :move="checkCanMoveTask" @start="isDragging = true" @end="isDragging = false"
              @change="onTaskDragChange($event, column)" class="kanban-column-body space-y-3 flex-1 min-h-0 rounded-lg">
              <template #header>
                <div v-if="addingColumnId === column.id"
                  class="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-3 space-y-3">
                  <div class="flex items-start gap-2">
                    <span class="text-gray-400 mt-2 flex-shrink-0">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z">
                        </path>
                      </svg>
                    </span>
                    <input v-model="quickAddTitle" type="text"
                      class="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter a task name * (required)" :disabled="quickAddLoading" />
                  </div>
                  <div class="flex items-center gap-2 text-sm text-gray-500">
                    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                      </path>
                    </svg>
                    <input v-model="quickAddDueDate" type="date"
                      class="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary"
                      :disabled="quickAddLoading" />
                    <span class="text-gray-400">Set due date</span>
                  </div>
                  <div class="flex flex-col gap-1">
                    <span class="text-xs text-gray-500">Assignees *</span>
                    <multiselect v-model="quickAddAssignees" :options="boardMembers" :multiple="true"
                      :close-on-select="false" :searchable="true" :disabled="quickAddLoading"
                      placeholder="Select one or more" label="name" track-by="id"
                      class="w-full quick-add-assignees-multiselect">
                      <template #option="{ option }">
                        <div class="flex flex-col py-1">
                          <span class="text-sm font-medium text-gray-900">{{ option.name }}</span>
                          <span class="text-xs text-gray-500">{{ option.email }}</span>
                        </div>
                      </template>
                      <template #noResult>
                        <span class="text-sm text-gray-500">No members found.</span>
                      </template>
                    </multiselect>
                  </div>
                  <div class="flex gap-2 pt-1">
                    <button @click="submitQuickAddTask(column)"
                      :disabled="quickAddLoading || !quickAddTitle.trim() || !quickAddAssignees || quickAddAssignees.length === 0"
                      class="flex-1 px-4 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {{ quickAddLoading ? 'Adding...' : 'Add task' }}
                    </button>
                    <button @click="closeQuickAddTask" :disabled="quickAddLoading"
                      class="px-4 py-2 text-gray-600 bg-gray-100 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
                <div v-else-if="getColumnLoading(column) && (!column.tasks || column.tasks.length === 0)"
                  class="kanban-empty-dropzone border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center py-12 text-sm text-gray-400 select-none">
                  <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-blue-500 mb-2"></div>
                  <span>Loading...</span>
                </div>
                <div v-else-if="!column.tasks || column.tasks.length === 0"
                  class="kanban-empty-dropzone border-2 border-dashed border-gray-200 rounded-lg flex flex-col items-center justify-center py-8 text-sm text-gray-400 select-none">
                  <button v-if="canUploadToBoard" @click="openQuickAddTask(column)"
                    class="flex items-center gap-2 px-4 py-2 text-primary hover:bg-primary/10 rounded-lg transition-colors">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    <span>Add task</span>
                  </button>
                  <span v-else>Drop tasks here</span>
                </div>
              </template>
              <template #item="{ element: task }">
                <div @click="handleTaskClick(task.id)"
                  class="bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-all p-4 cursor-pointer border-l-4 overflow-hidden"
                  :style="{ borderLeftColor: getPriorityColor(task.priority) }">
                  <div class="flex items-start justify-between mb-2">
                    <h4 class="font-semibold text-gray-900 text-sm flex-1">{{ task.title }}</h4>
                    <span class="text-xs px-2 py-1 rounded-full text-white ml-2"
                      :style="{ backgroundColor: getPriorityColor(task.priority) }">
                      {{ task.priority }}
                    </span>
                  </div>
                  <p v-if="task.description" class="text-xs text-gray-600 mb-3 task-card-description">
                    {{ task.description }}
                  </p>

                  <!-- Time Progress Bar (checklist excluded) -->
                  <div v-if="task.time_progress_percent != null"
                    :class="task.checklist_progress_percent != null ? 'mb-2' : 'mb-3'">
                    <div class="flex justify-between text-10px text-gray-500 mb-0.5">
                      <span>{{ task.time_progress_percent }}%</span>
                      <span v-if="task.estimated_minutes">{{ formatTimeSpent(task.actual_seconds_spent || 0) }} / {{
                        formatEstimatedDisplay(task.estimated_minutes, task.estimated_time_type) }}</span>
                    </div>
                    <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div class="task-progress-bar-fill h-full rounded-full transition-all duration-500 ease-out"
                        :class="[getProgressBarColorClass(task.time_progress_percent), { 'task-progress-bar-fill--animated': (task.status || '').toLowerCase() === 'in_progress' }, { 'task-progress-bar-fill--static': (task.status || '').toLowerCase() === 'paused' }]"
                        :style="{ width: Math.min(100, Math.max(0, task.time_progress_percent)) + '%' }"></div>
                    </div>
                  </div>
                  <!-- Checklist Progress Bar (separate, only when checklist exists) -->
                  <div v-if="task.checklist_progress_percent != null" class="mb-3">
                    <div class="flex justify-between text-10px text-gray-500 mb-0.5">
                      <span>Checklist {{ task.checklist_progress_percent }}%</span>
                    </div>
                    <div class="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                      <div class="h-full rounded-full transition-all duration-500 ease-out bg-emerald-500"
                        :style="{ width: Math.min(100, Math.max(0, task.checklist_progress_percent)) + '%' }"></div>
                    </div>
                  </div>

                  <!-- Creator and Assigned To Section -->
                  <div class="mb-3 space-y-1.5 min-w-0">
                    <!-- Creator -->
                    <div class="flex items-start gap-1.5 text-xs min-w-0">
                      <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      <span class="text-gray-500 flex-shrink-0">Created by</span>
                      <div
                        class="w-4 h-4 rounded-full bg-blue-500 text-white flex items-center justify-center text-8px flex-shrink-0">
                        {{ getInitials(task.created_by && task.created_by.name) }}
                      </div>
                      <span class="text-gray-700 break-words min-w-0">{{ (task.created_by && task.created_by.name) ||
                        'Unknown'
                      }}</span>
                    </div>

                    <!-- Assigned To -->
                    <div class="flex items-start gap-1.5 text-xs min-w-0">
                      <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                        </path>
                      </svg>
                      <span class="text-gray-500 flex-shrink-0">Assigned to</span>
                      <div v-if="task.assignees && task.assignees.length > 0"
                        class="flex items-start gap-1 min-w-0 flex-1">
                        <div class="flex -space-x-1 flex-shrink-0">
                          <div v-for="assignee in task.assignees.slice(0, 3)" :key="assignee.id"
                            class="w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center text-8px border border-white"
                            :title="assignee.name">
                            {{ getInitials(assignee.name) }}
                          </div>
                          <div v-if="task.assignees.length > 3"
                            class="w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-8px border border-white"
                            :title="`${task.assignees.length} assignees`">
                            +{{ task.assignees.length - 3 }}
                          </div>
                        </div>
                        <span class="text-gray-700 break-words ml-0.5 min-w-0">
                          {{task.assignees.map(a => a.name).join(', ')}}
                        </span>
                      </div>
                      <span v-else class="text-gray-400 italic">Unassigned</span>
                    </div>

                    <!-- Viewers -->
                    <div v-if="task.viewers && task.viewers.length > 0"
                      class="flex items-start gap-1.5 text-xs min-w-0">
                      <svg class="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z">
                        </path>
                      </svg>
                      <span class="text-gray-500 flex-shrink-0">Viewers</span>
                      <div class="flex items-start gap-1 min-w-0 flex-1">
                        <div class="flex -space-x-1 flex-shrink-0">
                          <div v-for="viewer in task.viewers.slice(0, 3)" :key="viewer.id"
                            class="w-4 h-4 rounded-full bg-amber-500 text-white flex items-center justify-center text-8px border border-white"
                            :title="viewer.name">
                            {{ getInitials(viewer.name) }}
                          </div>
                          <div v-if="task.viewers.length > 3"
                            class="w-4 h-4 rounded-full bg-gray-400 text-white flex items-center justify-center text-8px border border-white"
                            :title="`${task.viewers.length} viewers`">
                            +{{ task.viewers.length - 3 }}
                          </div>
                        </div>
                        <span class="text-gray-700 break-words ml-0.5 min-w-0">
                          {{task.viewers.map(v => v.name).join(', ')}}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Status badge (all statuses) -->
                  <div class="flex items-center gap-1 text-xs mb-1">
                    <span
                      :class="[getStatusBadgeClass(task.status), 'inline-flex items-center px-2.5 py-0.5 rounded-full font-medium']">{{
                        getStatusLabel(task.status) }}</span>
                  </div>

                  <!-- Completed date (Done tasks) -->
                  <div v-if="task.status === 'done' && task.completed_at"
                    class="flex items-center gap-1 text-xs text-green-600">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>Completed {{ formatDate(task.completed_at) }}</span>
                  </div>

                  <!-- Due Date -->
                  <div v-else-if="task.due_date" class="flex items-center gap-1 text-xs text-red-500">
                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z">
                      </path>
                    </svg>
                    <span>{{ formatDate(task.due_date) }}</span>
                  </div>
                </div>
              </template>
            </draggable>
            <!-- Load more (pagination for both tabs) -->
            <button v-if="getColumnHasMore(column)" @click="loadMoreColumnTasks(column)"
              :disabled="getColumnLoading(column)"
              class="mt-3 py-2 px-3 w-full rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-50 hover:bg-gray-100 border border-gray-200 text-gray-700">
              {{ getColumnLoading(column) ? 'Loading...' : 'Load more' }}
            </button>
          </div>

          <!-- Add new bucket -->
          <div v-if="canUploadToBoard" class="flex-shrink-0 w-80">
            <button @click="showAddBucketModal = true"
              class="w-full h-full min-h-[200px] rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50/50 transition-all flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-blue-600">
              <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              <span class="text-sm font-medium">Add a new bucket</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="text-center py-16">
        <p class="text-gray-600">Board not found</p>
      </div>

      <!-- Add Bucket Modal -->
      <base-modal v-if="showAddBucketModal && boardData" title="Add a new bucket" @close="closeAddBucketModal">
        <div class="p-4 sm:p-6">
          <form @submit.prevent="createBucket" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Bucket name *</label>
              <input v-model="newBucketName" type="text"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="e.g. Backlog, In Review" :disabled="addBucketLoading" />
              <p v-if="addBucketError" class="text-xs text-red-600 mt-1">{{ addBucketError }}</p>
            </div>
            <div class="flex justify-end gap-2 pt-2">
              <button type="button" @click="closeAddBucketModal"
                class="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button type="submit" :disabled="addBucketLoading || !newBucketName.trim()"
                class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {{ addBucketLoading ? 'Creating...' : 'Create bucket' }}
              </button>
            </div>
          </form>
        </div>
      </base-modal>

      <!-- Export Excel Modal -->
      <base-modal v-if="showExportModal && boardData" title="Export Excel" @close="closeExportModal">
        <export-tasks-modal :board-id="boardId" :board-members="boardMembers" :current-user-id="currentUserId"
          :current-user-name="currentUserName" @close="closeExportModal" />
      </base-modal>

      <!-- Create Task Modal -->
      <base-modal v-if="showCreateTaskModal && boardData" title="Create New Task" @close="showCreateTaskModal = false">
        <create-task-modal :board-id="boardId" :columns="columns" :board-members="boardMembers"
          @close="showCreateTaskModal = false" @created="handleTaskCreated" />
      </base-modal>

      <!-- Task Detail Modal -->
      <base-modal v-if="selectedTaskId && boardData" title="Task Details" @close="selectedTaskId = null">
        <task-detail-modal :task-id="selectedTaskId" :board-members="boardMembers" @close="selectedTaskId = null"
          @updated="handleTaskUpdated" @deleted="handleTaskDeleted" />
      </base-modal>

      <!-- Member Tasks Modal -->
      <base-full-modal v-if="selectedMemberForTasks" :title="`${selectedMemberForTasks.name} - Tasks`"
        @close="closeMemberTasksModal">
        <member-tasks-modal :member="selectedMemberForTasks" :board-id="boardId" :columns="columns"
          :member-stats="getMemberTaskStats(selectedMemberForTasks)" :board-members="boardMembers"
          :board-creator-id="boardCreatorId || (boardData && boardData.created_by ? boardData.created_by.id : null)"
          @close="closeMemberTasksModal" @task-updated="handleTaskUpdated" @task-deleted="handleTaskDeleted" />
      </base-full-modal>

      <!-- Attachment Preview Modal -->
      <base-modal v-if="showAttachmentPreviewModal" :title="attachmentPreviewTitle" width="90vw" height="85vh"
        @close="closeAttachmentPreview">
        <div class="p-4 flex flex-col attachment-preview-content">
          <div v-if="attachmentPreviewLoading" class="flex items-center justify-center py-12 flex-1">
            <div class="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-500"></div>
          </div>
          <div v-else-if="attachmentPreviewError" class="py-8 text-center text-red-600 flex-1">{{ attachmentPreviewError
            }}
          </div>
          <div v-else-if="attachmentPreviewUrl" class="flex justify-center flex-1 min-h-0">
            <img v-if="attachmentPreviewIsImage" :src="attachmentPreviewUrl"
              class="max-w-full object-contain attachment-preview-img" alt="Preview" />
            <iframe v-else-if="attachmentPreviewIsPdf" :src="attachmentPreviewUrl"
              class="w-full border-0 flex-1 attachment-preview-iframe" title="PDF Preview"></iframe>
            <p v-else class="text-gray-500 flex-1">Preview not available for this file type. Use download instead.</p>
          </div>
        </div>
      </base-modal>
    </div>
  </section>
</template>

<script>
import gql from 'graphql-tag';
import draggable from 'vuedraggable';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';
import CreateTaskModal from './components/CreateTaskModal.vue';
import ExportTasksModal from './components/ExportTasksModal.vue';
import TaskDetailModal from './components/TaskDetailModal.vue';
import MemberTasksModal from './components/MemberTasksModal.vue';
import MasterBoardModalPagination from './components/MasterBoardModalPagination.vue';
import BoardCalendarView from './components/BoardCalendarView.vue';
import IconCut from '../../components/icons/IconCut.vue';
import IconFolderPlus from '../../components/icons/IconFolderPlus.vue';

export default {
  name: 'task-board',
  components: {
    draggable,
    Multiselect,
    CreateTaskModal,
    ExportTasksModal,
    TaskDetailModal,
    MemberTasksModal,
    MasterBoardModalPagination,
    BoardCalendarView,
    IconCut,
    IconFolderPlus,
  },
  data() {
    return {
      boardId: null,
      showCreateTaskModal: false,
      showExportModal: false,
      selectedTaskId: null,
      selectedMemberForTasks: null,
      localColumns: [],
      isDragging: false,
      activeTab: 'my',
      board: null,
      boardLoading: false,
      statistics: null,
      statisticsLoading: false,
      boardMembersData: [],
      boardMembersLoading: false,
      boardCreatorId: null,
      myTasksData: {},
      myTasksLoading: {},
      allTasksData: {},
      allTasksLoading: {},
      viewerTasksData: {},
      viewerTasksLoading: {},
      showAttachmentPreviewModal: false,
      attachmentPreviewLoading: false,
      attachmentPreviewError: null,
      attachmentPreviewUrl: null,
      attachmentPreviewAttachment: null,
      boardUploading: false,

      boardFolderCreating: false,
      currentAttachmentFolderId: null,
      archiveSelectedItems: [],
      archiveSelectionAnchor: null,
      archiveMarquee: {
        active: false,
        startX: 0,
        startY: 0,
        currentX: 0,
        currentY: 0,
      },
      archiveSuppressNextSelectionClear: false,
      macFolderIcon: require('../../assets/images/default/mac-folder.png'),
      inlineRenamingFolderId: null,
      inlineRenamingFolderName: '',
      inlineRenamingFolderSaving: false,
      inlineRenamingAttachmentId: null,
      inlineRenamingAttachmentName: '',
      inlineRenamingAttachmentSaving: false,
      archiveContextMenu: {
        visible: false,
        x: 0,
        y: 0,
        type: null,
        item: null,
      },
      archiveClipboard: {
        mode: null,
        items: [],
        sourceFolderId: null,
      },
      archivePasting: false,
      showAddBucketModal: false,
      newBucketName: '',
      addBucketLoading: false,
      addBucketError: null,
      editingColumnId: null,
      editBucketName: '',
      editBucketLoading: false,
      editBucketError: null,
      addingColumnId: null,
      quickAddTitle: '',
      quickAddDueDate: '',
      quickAddAssignees: [],
      quickAddLoading: false,
      boardTaskSearch: '',
      boardTasksFetchBatchId: 0,
      boardSearchDebounceTimer: null,
      taskViewMode: 'grid',
      listTasks: [],
      listTasksLoading: false,
      listPaginatorInfo: null,
      listTasksFetchBatchId: 0,
      calendarTasks: [],
      calendarTasksLoading: false,
      calendarTasksFetchBatchId: 0,
    };
  },
  computed: {
    attachmentPreviewTitle() {
      const att = this.attachmentPreviewAttachment;
      return att ? (att.display_name || att.original_filename || 'Attachment Preview') : 'Attachment Preview';
    },
    attachmentPreviewIsImage() {
      const att = this.attachmentPreviewAttachment;
      if (!att) return false;
      const t = att.file_type ? String(att.file_type).toLowerCase() : '';
      if (t.indexOf('image/') === 0 || t === 'image/jpeg' || t === 'image/png' || t === 'image/gif' || t === 'image/webp') return true;
      const fn = att.original_filename || att.file_name || '';
      const ext = fn ? String(fn).split('.').pop().toLowerCase() : '';
      return ['jpg', 'jpeg', 'png', 'gif', 'webp'].indexOf(ext) !== -1;
    },
    attachmentPreviewIsPdf() {
      const att = this.attachmentPreviewAttachment;
      if (!att) return false;
      const t = att.file_type ? String(att.file_type).toLowerCase() : '';
      if (t === 'application/pdf' || t.indexOf('pdf') !== -1) return true;
      const fn = att.original_filename || att.file_name || '';
      return fn ? String(fn).split('.').pop().toLowerCase() === 'pdf' : false;
    },
    boardData() {
      if (!this.board || this.board.error) {
        return null;
      }
      return this.board.data;
    },
    attachments() {
      const data = this.boardData;
      if (!data || !data.attachments || !Array.isArray(data.attachments)) return [];
      return data.attachments;
    },
    attachmentFolders() {
      const data = this.boardData;
      if (!data || !data.attachment_folders || !Array.isArray(data.attachment_folders)) return [];
      return data.attachment_folders;
    },
    currentFolders() {
      const currentId = this.currentAttachmentFolderId ? String(this.currentAttachmentFolderId) : null;
      return this.attachmentFolders.filter(function (folder) {
        const parentId = folder.parent_folder_id ? String(folder.parent_folder_id) : null;
        return parentId === currentId;
      });
    },
    currentAttachments() {
      const currentId = this.currentAttachmentFolderId ? String(this.currentAttachmentFolderId) : null;
      return this.attachments.filter(function (att) {
        const folderId = att.folder_id ? String(att.folder_id) : null;
        return folderId === currentId;
      });
    },
    hasCurrentArchiveItems() {
      return this.currentFolders.length > 0 || this.currentAttachments.length > 0;
    },
    attachmentBreadcrumb() {
      const crumbs = [];
      let folderId = this.currentAttachmentFolderId;
      const folders = this.attachmentFolders;
      const maxDepth = 50;
      let depth = 0;
      while (folderId && depth < maxDepth) {
        const idStr = String(folderId);
        let found = null;
        for (let i = 0; i < folders.length; i++) {
          if (String(folders[i].id) === idStr) {
            found = folders[i];
            break;
          }
        }
        if (!found) break;
        crumbs.unshift({ id: found.id, name: found.name });
        folderId = found.parent_folder_id || null;
        depth++;
      }
      return crumbs;
    },
    canEditAllBoards() {
      const permissions = this.$store.getters.permissions || '';
      const permissionsArray = permissions.split(',');
      return permissionsArray.includes('view-edit-task-management-board');
    },
    currentUserId() {
      const uid = this.$store.getters.userId;
      if (!uid) return null;
      return parseInt(uid);
    },
    currentUserName() {
      return this.$store.getters.name || '';
    },
    canEditMasterBoard() {
      const data = this.boardData;
      if (!data || parseInt(data.id) === 0) return false;
      const perms = this.$store.getters.permissions || '';
      if (perms.split(',').indexOf('view-edit-task-management-board') !== -1) return true;
      const uid = this.$store.getters.userId;
      if (!data.created_by || !data.created_by.id || !uid) return false;
      return parseInt(data.created_by.id) === parseInt(uid);
    },
    canUploadToBoard() {
      if (!this.currentUserId || !this.boardData) return false;
      if (this.canEditAllBoards) return true;
      const creatorId = this.boardCreatorId || (this.boardData.created_by && this.boardData.created_by.id ? parseInt(this.boardData.created_by.id) : null);
      if (creatorId && parseInt(creatorId) === this.currentUserId) return true;
      return this.boardMembers.some(m => parseInt(m.id) === this.currentUserId);
    },
    archivePastingLabel() {
      if (this.archivePasting) {
        const clip = this.archiveClipboard;
        if (clip && clip.mode === 'cut') return 'Moving...';
        return 'Pasting...';
      }
      if (this.boardFolderCreating) return 'Creating folder...';
      return 'Uploading...';
    },
    archiveMarqueeStyle() {
      const marquee = this.archiveMarquee;
      if (!marquee.active) {
        return { display: 'none' };
      }
      const left = Math.min(marquee.startX, marquee.currentX);
      const top = Math.min(marquee.startY, marquee.currentY);
      const width = Math.abs(marquee.currentX - marquee.startX);
      const height = Math.abs(marquee.currentY - marquee.startY);
      return {
        left: left + 'px',
        top: top + 'px',
        width: width + 'px',
        height: height + 'px',
      };
    },
    archiveContextMenuActions() {
      const menu = this.archiveContextMenu;
      if (!menu.visible) return [];
      if (menu.type === 'background') {
        const actions = [];
        if (this.canUploadToBoard) {
          actions.push({ id: 'new-folder', label: 'New Folder' });
        }
        if (this.canPasteArchiveClipboard()) {
          actions.push({ id: 'paste', label: 'Paste' });
        }
        return actions;
      }
      if (!menu.item) return [];
      const actions = [];
      const isMulti = this.archiveSelectedItems.length > 1;
      if (!isMulti) {
        actions.push({ id: 'open', label: 'Open' });
        if (menu.type === 'file' || this.isAttachmentFolderDownloadable(menu.item)) {
          actions.push({ id: 'download', label: 'Download' });
        }
      } else {
        const downloadCount = this.getDownloadableSelectedCount();
        if (downloadCount > 0) {
          actions.push({
            id: 'download',
            label: downloadCount > 1 ? 'Download (' + downloadCount + ')' : 'Download',
          });
        }
      }
      if (this.canUploadToBoard) {
        actions.push({ id: 'cut', label: isMulti ? 'Cut (' + this.archiveSelectedItems.length + ')' : 'Cut' });
        actions.push({ id: 'copy', label: isMulti ? 'Copy (' + this.archiveSelectedItems.length + ')' : 'Copy' });
      }
      const deletableCount = this.getDeletableSelectedCount();
      if (deletableCount > 0) {
        actions.push({
          id: 'delete',
          label: deletableCount > 1 ? 'Delete (' + deletableCount + ')' : 'Delete',
          danger: true,
        });
      }
      if (!isMulti && menu.type === 'folder' && this.canEditAttachmentFolder(menu.item)) {
        actions.push({ id: 'rename', label: 'Rename' });
      } else if (!isMulti && menu.type === 'file' && this.canEditAttachment(menu.item)) {
        actions.push({ id: 'rename', label: 'Rename' });
      }
      return actions;
    },
    columnNameToStatus() {
      return {
        'To Do': 'todo',
        'In Progress': 'in_progress',
        'In Review': 'in_review',
        'Done': 'done',
      };
    },
    columns() {
      if (!this.boardData || !this.boardData.columns) {
        return [];
      }
      // Return a sorted copy to avoid mutating original data (no side effects)
      return [...this.boardData.columns].sort((a, b) => (a.order || 0) - (b.order || 0));
    },
    boardMembers() {
      if (this.boardMembersData && this.boardMembersData.length > 0) {
        return this.boardMembersData;
      }
      return [];
    },
    memberTaskStats() {
      const stats = {};
      if (!this.boardData || !this.boardData.columns) return stats;

      this.boardMembers.forEach(member => {
        const memberId = parseInt(member.id);
        stats[memberId] = {
          total: 0,
          todo: 0,
          in_progress: 0,
          paused: 0,
          in_review: 0,
          done: 0,
        };
      });

      this.boardData.columns.forEach(column => {
        if (!column.tasks || !Array.isArray(column.tasks)) return;
        column.tasks.forEach(task => {
          const status = (task.status || '').toLowerCase().replace(/\s+/g, '_');
          if (!task.assignees || !task.assignees.length) return;
          task.assignees.forEach(assignee => {
            const assigneeId = parseInt(assignee.id);
            if (!stats[assigneeId]) return;
            stats[assigneeId].total += 1;
            if (status === 'todo') stats[assigneeId].todo += 1;
            else if (status === 'in_progress') stats[assigneeId].in_progress += 1;
            else if (status === 'paused') stats[assigneeId].paused += 1;
            else if (status === 'in_review') stats[assigneeId].in_review += 1;
            else if (status === 'done') stats[assigneeId].done += 1;
          });
        });
      });

      return stats;
    },
    statisticsData() {
      return this.statistics;
    },
    bucketChartSeries() {
      if (!this.statisticsData || !this.statisticsData.bucket_stats || this.statisticsData.bucket_stats.length === 0) {
        return [{ data: [] }];
      }
      const stats = this.statisticsData.bucket_stats;
      return [{
        name: 'Tasks',
        data: stats.map((b) => b.count || 0),
      }];
    },
    bucketChartOptions() {
      const categories = (this.statisticsData && this.statisticsData.bucket_stats)
        ? this.statisticsData.bucket_stats.map((b) => b.name || 'Unnamed')
        : [];
      const palette = ['#499DD4', '#10B981', '#F59E0B', '#7c3aed', '#EC4899', '#6366f1', '#14b8a6', '#f97316'];
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        xaxis: {
          categories: categories,
          title: {
            text: 'Bucket',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
          },
        },
        yaxis: {
          title: {
            text: 'Number of Tasks',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
          },
        },
        colors: categories.map((_, i) => palette[i % palette.length]),
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            distributed: true,
          },
        },
      };
    },
    statusChartSeries() {
      if (!this.statisticsData || !this.statisticsData.status_stats) {
        return [{ data: [] }];
      }
      const stats = this.statisticsData.status_stats;
      return [{
        name: 'Tasks',
        data: [
          stats.todo || 0,
          stats.in_progress || 0,
          stats.paused || 0,
          stats.in_review || 0,
          stats.done || 0,
        ]
      }];
    },
    statusChartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        xaxis: {
          categories: ['To Do', 'In Progress', 'Paused', 'In Review', 'Done'],
          title: {
            text: 'Status',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
          },
        },
        yaxis: {
          title: {
            text: 'Number of Tasks',
            style: {
              fontSize: '12px',
              fontWeight: 'bold',
            },
          },
        },
        colors: ['#9BA4B5', '#499DD4', '#F59E0B', '#695DBF', '#10B981'],
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            distributed: true,
          },
        },
      };
    },
    priorityChartSeries() {
      if (!this.statisticsData || !this.statisticsData.priority_stats) {
        return [];
      }
      const stats = this.statisticsData.priority_stats;
      return [
        stats.low || 0,
        stats.medium || 0,
        stats.high || 0,
        stats.critical || 0,
      ];
    },
    priorityChartOptions() {
      return {
        chart: {
          type: 'donut',
          toolbar: { show: false },
        },
        labels: ['Low', 'Medium', 'High', 'Critical'],
        colors: ['#10B981', '#499DD4', '#F59E0B', '#EF4444'],
        legend: {
          position: 'bottom',
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(0) + '%';
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
            },
          },
        },
      };
    },
    memberChartSeries() {
      if (!this.boardMembers || this.boardMembers.length === 0) {
        return [{ name: 'Total Tasks', data: [] }];
      }
      let data;
      if (this.activeTab === 'viewer') {
        data = this.boardMembers.map(member => this.getViewerMemberTaskCount(member.id));
      } else {
        data = this.boardMembers.map(member => {
          const stats = this.getMemberTaskStats(member);
          return stats.total;
        });
      }
      return [{ name: 'Total Tasks', data }];
    },
    memberChartOptions() {
      const categories = (this.boardMembers || []).map(m => (m.name || 'Unknown').substring(0, 20));
      const palette = ['#499DD4', '#10B981', '#F59E0B', '#7c3aed', '#EC4899', '#6366f1', '#14b8a6', '#f97316'];
      const count = categories.length;
      const colors = Array.from({ length: count }, (_, i) => palette[i % palette.length]);
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
        },
        xaxis: {
          categories,
          labels: {
            rotate: -45,
            rotateAlways: categories.length > 5,
          },
          title: {
            text: 'Board Member',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
        },
        yaxis: {
          title: {
            text: 'Number of Tasks',
            style: { fontSize: '12px', fontWeight: 'bold' },
          },
        },
        colors,
        dataLabels: {
          enabled: true,
        },
        plotOptions: {
          bar: {
            columnWidth: '60%',
            distributed: true,
          },
        },
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClickClearArchiveSelection);
    document.addEventListener('keydown', this.onDocumentKeydownArchive);
    document.addEventListener('scroll', this.closeArchiveContextMenu, true);
    this.boardId = parseInt(this.$route.params.id);
    // Fetch board and statistics on initial load
    this.fetchBoard();
    this.fetchStatistics();
  },
  watch: {
    '$route.params.id'(newId) {
      this.boardId = parseInt(newId);
      this.boardMembersData = [];
      this.boardCreatorId = null;
      this.boardTaskSearch = '';
      this.taskViewMode = 'grid';
      this.listTasks = [];
      this.listPaginatorInfo = null;
      this.calendarTasks = [];
      if (this.boardSearchDebounceTimer) {
        clearTimeout(this.boardSearchDebounceTimer);
        this.boardSearchDebounceTimer = null;
      }
      this.resetMyTasksData();
      this.resetAllTasksData();
      this.fetchBoard();
      this.fetchStatistics();
    },
    boardData: {
      handler(newVal) {
        if (this.activeTab === 'all' || this.activeTab === 'viewer') {
          this.updateLocalColumns(newVal);
        } else if (newVal && this.activeTab === 'my') {
          this.updateLocalColumns(newVal);
        }
      },
      immediate: true,
    },
    activeTab: {
      handler() {
        if (this.activeTab === 'my') {
          this.resetMyTasksData();
        } else if (this.activeTab === 'viewer') {
          this.resetViewerTasksData();
        } else {
          this.resetAllTasksData();
        }
        this.refreshTasks();
      },
    },
    boardTaskSearch() {
      this.scheduleBoardTaskSearchRefetch();
    },
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClickClearArchiveSelection);
    document.removeEventListener('keydown', this.onDocumentKeydownArchive);
    document.removeEventListener('scroll', this.closeArchiveContextMenu, true);
    this.teardownArchiveMarqueeListeners();
    if (this.boardSearchDebounceTimer) {
      clearTimeout(this.boardSearchDebounceTimer);
      this.boardSearchDebounceTimer = null;
    }
  },
  methods: {
    saveDownloadFromBase64(base64Content, fileType, filename) {
      const binaryString = atob(base64Content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: fileType || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    async viewAttachment(attachment) {
      this.cancelInlineRenameAttachment();
      this.cancelInlineRenameAttachmentFolder();

      this.attachmentPreviewAttachment = attachment;
      this.attachmentPreviewLoading = true;
      this.attachmentPreviewError = null;
      this.attachmentPreviewUrl = null;
      this.showAttachmentPreviewModal = true;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskBoardAttachmentContent($id: ID!) {
              getTaskBoardAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment { id original_filename }
                }
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.getTaskBoardAttachmentContent;
        if (payload && payload.error) {
          this.attachmentPreviewError = payload.message || 'Failed to load preview.';
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          this.attachmentPreviewError = 'Failed to load preview.';
          return;
        }

        const base64Content = contentData.content;
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const mimeType = contentData.file_type || 'application/octet-stream';
        const blob = new Blob([bytes], { type: mimeType });
        this.attachmentPreviewUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error loading attachment for preview:', error);
        this.attachmentPreviewError = 'Failed to load preview. Please try again.';
      } finally {
        this.attachmentPreviewLoading = false;
      }
    },
    closeAttachmentPreview() {
      if (this.attachmentPreviewUrl) {
        URL.revokeObjectURL(this.attachmentPreviewUrl);
      }
      this.showAttachmentPreviewModal = false;
      this.attachmentPreviewAttachment = null;
      this.attachmentPreviewUrl = null;
      this.attachmentPreviewError = null;
    },
    async downloadAttachment(attachment) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskBoardAttachmentContent($id: ID!) {
              getTaskBoardAttachmentContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  attachment { id original_filename }
                }
              }
            }
          `,
          variables: { id: attachment.id },
        });

        const payload = data && data.getTaskBoardAttachmentContent;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to download attachment.');
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          alert('Failed to download attachment.');
          return;
        }

        const downloadName = (contentData.attachment && contentData.attachment.original_filename)
          || attachment.original_filename
          || 'download';
        this.saveDownloadFromBase64(contentData.content, contentData.file_type, downloadName);
      } catch (error) {
        console.error('Error downloading attachment:', error);
        alert('Failed to download attachment. Please try again.');
      }
    },
    async downloadAttachmentFolder(folder) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation GetTaskBoardAttachmentFolderContent($id: ID!) {
              getTaskBoardAttachmentFolderContent(id: $id) {
                error
                error_code
                message
                data {
                  content
                  file_type
                  download_filename
                  folder { id name }
                }
              }
            }
          `,
          variables: { id: folder.id },
        });

        const payload = data && data.getTaskBoardAttachmentFolderContent;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to download folder.');
          return;
        }

        const contentData = payload && payload.data;
        if (!contentData || !contentData.content) {
          alert('Failed to download folder.');
          return;
        }

        const downloadName = contentData.download_filename
          || ((contentData.folder && contentData.folder.name) ? contentData.folder.name + '.zip' : null)
          || (folder.name ? folder.name + '.zip' : 'folder.zip');
        this.saveDownloadFromBase64(contentData.content, contentData.file_type || 'application/zip', downloadName);
      } catch (error) {
        console.error('Error downloading folder:', error);
        alert('Failed to download folder. Please try again.');
      }
    },
    canEditAttachment(att) {
      if (!att || !this.currentUserId) return false;
      const uploaderId = att.uploaded_by && att.uploaded_by.id ? parseInt(att.uploaded_by.id) : null;
      return uploaderId === this.currentUserId;
    },
    canEditAttachmentFolder(folder) {
      if (!folder || !this.currentUserId) return false;
      if (this.canEditAllBoards) return true;
      const creatorId = folder.created_by && folder.created_by.id ? parseInt(folder.created_by.id) : null;
      return creatorId === this.currentUserId;
    },
    openAttachmentFolder(folder) {
      if (!folder || !folder.id) return;
      this.currentAttachmentFolderId = folder.id;
      this.clearArchiveSelection();
      this.cancelInlineRenameAttachmentFolder();
      this.cancelInlineRenameAttachment();
    },
    navigateAttachmentFolder(folderId) {
      this.currentAttachmentFolderId = folderId;
      this.clearArchiveSelection();
      this.cancelInlineRenameAttachmentFolder();
      this.cancelInlineRenameAttachment();
    },
    async createAttachmentFolder() {
      if (!this.boardId || !this.boardData) return;
      this.boardFolderCreating = true;
      try {
        const variables = { board_id: this.boardId };
        if (this.currentAttachmentFolderId) {
          variables.parent_folder_id = this.currentAttachmentFolderId;
        }
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateTaskBoardAttachmentFolder($board_id: ID!, $parent_folder_id: ID) {
              createTaskBoardAttachmentFolder(board_id: $board_id, parent_folder_id: $parent_folder_id) {
                error
                error_code
                message
                data { id name parent_folder_id }
              }
            }
          `,
          variables: variables,
        });
        const payload = data && data.createTaskBoardAttachmentFolder;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to create folder.');
          return;
        }
        await this.fetchBoard();
      } catch (error) {
        console.error('Error creating folder:', error);
        alert('Failed to create folder. Please try again.');
      } finally {
        this.boardFolderCreating = false;
      }
    },
    async onBoardAttachmentSelected(event) {
      const input = event.target;
      const file = input && input.files && input.files[0] ? input.files[0] : null;
      if (!file || !this.boardData || !this.boardId) return;
      this.boardUploading = true;
      try {
        const uploadVariables = {
          board_id: this.boardId,
          file: file,
        };
        if (this.currentAttachmentFolderId) {
          uploadVariables.folder_id = this.currentAttachmentFolderId;
        }
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UploadTaskBoardAttachment($board_id: ID!, $folder_id: ID, $file: Upload!) {
              uploadTaskBoardAttachment(board_id: $board_id, folder_id: $folder_id, file: $file) {
                error
                error_code
                message
                data { id file_name original_filename display_name folder_id }
              }
            }
          `,
          variables: uploadVariables,
        });
        const payload = data && data.uploadTaskBoardAttachment;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to upload attachment.');
          return;
        }
        await this.fetchBoard();
      } catch (error) {
        console.error('Error uploading attachment:', error);
        alert('Failed to upload attachment. Please try again.');
      } finally {
        this.boardUploading = false;
        if (input) input.value = '';
      }
    },
    getAttachmentDisplayName(att) {
      if (!att) return '';
      return att.display_name || att.original_filename || att.file_name || '';
    },
    getAttachmentOriginalName(att) {
      if (!att) return '';
      return att.original_filename || att.file_name || '';
    },
    getFolderContentsLabel(folderId) {
      const counts = this.getFolderDirectContents(folderId);
      const parts = [];
      if (counts.subfolders > 0) {
        parts.push(counts.subfolders + ' ' + (counts.subfolders === 1 ? 'folder' : 'folders'));
      }
      if (counts.files > 0) {
        parts.push(counts.files + ' ' + (counts.files === 1 ? 'file' : 'files'));
      }
      if (parts.length === 0) return 'Empty';
      return parts.join(', ');
    },
    getFolderDirectContents(folderId) {
      const idStr = String(folderId);
      let subfolders = 0;
      let files = 0;
      const folders = this.attachmentFolders;
      for (let i = 0; i < folders.length; i++) {
        const folder = folders[i];
        const parentId = folder.parent_folder_id ? String(folder.parent_folder_id) : null;
        if (parentId === idStr) subfolders++;
      }
      const attachments = this.attachments;
      for (let j = 0; j < attachments.length; j++) {
        const att = attachments[j];
        const attFolderId = att.folder_id ? String(att.folder_id) : null;
        if (attFolderId === idStr) files++;
      }
      return { subfolders: subfolders, files: files };
    },
    formatArchiveFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
    formatArchiveDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatArchiveMimeType(mimeType) {
      const ft = mimeType ? String(mimeType).toLowerCase() : '';
      if (!ft) return 'Unknown';
      if (ft === 'application/pdf') return 'PDF Document';
      if (ft === 'application/msword') return 'Word Document';
      if (ft === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return 'Word Document';
      if (ft === 'application/vnd.ms-excel') return 'Excel Spreadsheet';
      if (ft === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return 'Excel Spreadsheet';
      if (ft === 'application/vnd.ms-powerpoint') return 'PowerPoint Presentation';
      if (ft === 'application/vnd.openxmlformats-officedocument.presentationml.presentation') return 'PowerPoint Presentation';
      if (ft === 'text/plain') return 'Plain Text';
      if (ft === 'text/csv' || ft === 'application/csv') return 'CSV File';
      if (ft.indexOf('image/') === 0) {
        const subtype = ft.split('/').pop();
        if (subtype) return subtype.toUpperCase() + ' Image';
        return 'Image';
      }
      return ft;
    },
    getArchiveFileExtension(att) {
      // Extension badge and icon must not depend on `display_name`,
      // because display_name can be renamed without extension.
      // We prefer the stored original filename, and fall back to MIME type.
      if (!att) return 'FILE';

      const fileName = att.original_filename || att.file_name || '';
      if (fileName) {
        const parts = String(fileName).split('.');
        if (parts.length >= 2) {
          const ext = parts[parts.length - 1].toLowerCase();
          if (ext) {
            return ext.length > 4 ? ext.substring(0, 4).toUpperCase() : ext.toUpperCase();
          }
        }
      }

      const ft = (att.file_type || '').toLowerCase();
      // Basic MIME-type fallbacks for common types.
      if (ft.indexOf('image/') === 0) {
        const subtype = ft.split('/').pop();
        if (subtype) return subtype.length > 4 ? subtype.substring(0, 4).toUpperCase() : subtype.toUpperCase();
        return 'IMG';
      }
      if (ft === 'application/pdf') return 'PDF';

      return 'FILE';
    },
    getArchiveFileIconClass(att) {
      const ft = att && att.file_type ? String(att.file_type).toLowerCase() : '';

      // Strict MIME-type based coloring.
      if (ft === 'application/pdf') return 'archive-file-pdf';

      if (
        ft === 'application/msword' ||
        ft === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        return 'archive-file-doc';
      }

      if (
        ft === 'application/vnd.ms-excel' ||
        ft === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        ft === 'text/csv' ||
        ft === 'application/csv'
      ) {
        return 'archive-file-sheet';
      }

      if (
        ft === 'application/vnd.ms-powerpoint' ||
        ft === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
      ) {
        return 'archive-file-ppt';
      }

      if (ft.indexOf('image/') === 0) return 'archive-file-image';
      if (ft === 'text/plain') return 'archive-file-txt';

      return 'archive-file-default';
    },
    clearArchiveSelection() {
      this.archiveSelectedItems = [];
      this.archiveSelectionAnchor = null;
    },
    getArchiveSelectionKey(type, id) {
      return type + ':' + String(id);
    },
    setArchiveSelection(items, anchor) {
      const normalized = [];
      const seen = {};
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item || !item.type || !item.id) continue;
        const key = this.getArchiveSelectionKey(item.type, item.id);
        if (seen[key]) continue;
        seen[key] = true;
        normalized.push({ type: item.type, id: String(item.id) });
      }
      this.archiveSelectedItems = normalized;
      if (anchor && anchor.type && anchor.id) {
        this.archiveSelectionAnchor = { type: anchor.type, id: String(anchor.id) };
      } else if (normalized.length > 0) {
        this.archiveSelectionAnchor = { type: normalized[0].type, id: normalized[0].id };
      } else {
        this.archiveSelectionAnchor = null;
      }
    },
    isArchiveItemSelected(type, id) {
      const key = this.getArchiveSelectionKey(type, id);
      const items = this.archiveSelectedItems;
      for (let i = 0; i < items.length; i++) {
        if (this.getArchiveSelectionKey(items[i].type, items[i].id) === key) {
          return true;
        }
      }
      return false;
    },
    getArchiveDisplayOrderItems() {
      const items = [];
      const folders = this.currentFolders;
      const files = this.currentAttachments;
      for (let i = 0; i < folders.length; i++) {
        items.push({ type: 'folder', id: String(folders[i].id) });
      }
      for (let j = 0; j < files.length; j++) {
        items.push({ type: 'file', id: String(files[j].id) });
      }
      return items;
    },
    handleArchiveItemSelection(type, id, event) {
      const idStr = String(id);
      const isMod = event && (event.ctrlKey || event.metaKey);
      const isShift = event && event.shiftKey;
      if (isShift) {
        this.selectArchiveItemRange(type, idStr);
        return;
      }
      if (isMod) {
        this.toggleArchiveItemSelection(type, idStr);
        return;
      }
      this.setArchiveSelection([{ type: type, id: idStr }], { type: type, id: idStr });
    },
    toggleArchiveItemSelection(type, id) {
      const idStr = String(id);
      const key = this.getArchiveSelectionKey(type, idStr);
      const items = this.archiveSelectedItems.slice();
      let foundIndex = -1;
      for (let i = 0; i < items.length; i++) {
        if (this.getArchiveSelectionKey(items[i].type, items[i].id) === key) {
          foundIndex = i;
          break;
        }
      }
      if (foundIndex !== -1) {
        items.splice(foundIndex, 1);
        this.archiveSelectedItems = items;
        if (items.length === 0) {
          this.archiveSelectionAnchor = null;
        }
        return;
      }
      items.push({ type: type, id: idStr });
      this.archiveSelectedItems = items;
      this.archiveSelectionAnchor = { type: type, id: idStr };
    },
    selectArchiveItemRange(type, id) {
      const idStr = String(id);
      const anchor = this.archiveSelectionAnchor;
      if (!anchor) {
        this.setArchiveSelection([{ type: type, id: idStr }], { type: type, id: idStr });
        return;
      }
      const ordered = this.getArchiveDisplayOrderItems();
      const targetKey = this.getArchiveSelectionKey(type, idStr);
      const anchorKey = this.getArchiveSelectionKey(anchor.type, anchor.id);
      let anchorIndex = -1;
      let targetIndex = -1;
      for (let i = 0; i < ordered.length; i++) {
        const itemKey = this.getArchiveSelectionKey(ordered[i].type, ordered[i].id);
        if (itemKey === anchorKey) anchorIndex = i;
        if (itemKey === targetKey) targetIndex = i;
      }
      if (anchorIndex === -1 || targetIndex === -1) {
        this.setArchiveSelection([{ type: type, id: idStr }], { type: type, id: idStr });
        return;
      }
      const start = Math.min(anchorIndex, targetIndex);
      const end = Math.max(anchorIndex, targetIndex);
      this.setArchiveSelection(ordered.slice(start, end + 1), anchor);
    },
    prepareArchiveContextMenuSelection(type, id) {
      if (!this.isArchiveItemSelected(type, id)) {
        this.setArchiveSelection([{ type: type, id: String(id) }], { type: type, id: String(id) });
      }
    },
    getArchiveItemRecord(type, id) {
      const idStr = String(id);
      if (type === 'folder') {
        const folders = this.attachmentFolders;
        for (let i = 0; i < folders.length; i++) {
          if (String(folders[i].id) === idStr) return folders[i];
        }
        return null;
      }
      if (type === 'file') {
        const attachments = this.attachments;
        for (let j = 0; j < attachments.length; j++) {
          if (String(attachments[j].id) === idStr) return attachments[j];
        }
        return null;
      }
      return null;
    },
    getSelectedArchiveItemsWithMeta() {
      const result = [];
      const items = this.archiveSelectedItems;
      for (let i = 0; i < items.length; i++) {
        const selected = items[i];
        const record = this.getArchiveItemRecord(selected.type, selected.id);
        if (!record) continue;
        const name = selected.type === 'folder'
          ? (record.name || '')
          : this.getAttachmentDisplayName(record);
        result.push({
          type: selected.type,
          id: selected.id,
          name: name,
        });
      }
      return result;
    },
    onArchiveGridItemClick(event, type, id) {
      this.handleArchiveItemSelection(type, id, event);
    },
    onArchiveLabelClick(event, type, item) {
      if (!item || !item.id) return;
      const wasOnlySelected = this.archiveSelectedItems.length === 1 && this.isArchiveItemSelected(type, item.id);
      this.handleArchiveItemSelection(type, item.id, event);
      if (!wasOnlySelected) return;
      if (type === 'folder') {
        this.startInlineRenameAttachmentFolder(item);
      } else if (type === 'file') {
        this.startInlineRenameAttachment(item);
      }
    },
    onArchiveGridMouseDown(event) {
      if (!event || event.button !== 0) return;
      if (event.target && event.target.closest('.archive-grid-item')) return;
      if (event.target && event.target.closest('.archive-item-kebab-menu')) return;
      this.clearArchiveSelection();
      event.preventDefault();
      this.archiveMarquee.active = true;
      this.archiveMarquee.startX = event.clientX;
      this.archiveMarquee.startY = event.clientY;
      this.archiveMarquee.currentX = event.clientX;
      this.archiveMarquee.currentY = event.clientY;
      document.addEventListener('mousemove', this.onArchiveMarqueeMouseMove);
      document.addEventListener('mouseup', this.onArchiveMarqueeMouseUp);
    },
    onArchiveMarqueeMouseMove(event) {
      if (!this.archiveMarquee.active || !event) return;
      this.archiveMarquee.currentX = event.clientX;
      this.archiveMarquee.currentY = event.clientY;
      const selected = this.getArchiveMarqueeSelectedItems();
      if (selected.length > 0) {
        this.setArchiveSelection(selected, selected[0]);
      } else {
        this.archiveSelectedItems = [];
        this.archiveSelectionAnchor = null;
      }
    },
    onArchiveMarqueeMouseUp() {
      if (!this.archiveMarquee.active) return;
      const marqueeRect = this.getArchiveMarqueeRect();
      const hadMarqueeDrag = marqueeRect.width >= 4 || marqueeRect.height >= 4;
      this.commitArchiveMarqueeSelection();
      this.teardownArchiveMarqueeListeners();
      this.archiveMarquee.active = false;
      if (hadMarqueeDrag) {
        this.archiveSuppressNextSelectionClear = true;
      }
    },
    teardownArchiveMarqueeListeners() {
      document.removeEventListener('mousemove', this.onArchiveMarqueeMouseMove);
      document.removeEventListener('mouseup', this.onArchiveMarqueeMouseUp);
    },
    getArchiveMarqueeRect() {
      const marquee = this.archiveMarquee;
      const left = Math.min(marquee.startX, marquee.currentX);
      const top = Math.min(marquee.startY, marquee.currentY);
      const right = Math.max(marquee.startX, marquee.currentX);
      const bottom = Math.max(marquee.startY, marquee.currentY);
      return {
        left: left,
        top: top,
        right: right,
        bottom: bottom,
        width: right - left,
        height: bottom - top,
      };
    },
    archiveRectsIntersect(rectA, rectB) {
      return !(
        rectA.right < rectB.left ||
        rectA.left > rectB.right ||
        rectA.bottom < rectB.top ||
        rectA.top > rectB.bottom
      );
    },
    commitArchiveMarqueeSelection() {
      const selected = this.getArchiveMarqueeSelectedItems();
      if (selected.length > 0) {
        this.setArchiveSelection(selected, selected[0]);
      }
    },
    getArchiveMarqueeSelectedItems() {
      const marqueeRect = this.getArchiveMarqueeRect();
      if (marqueeRect.width < 4 && marqueeRect.height < 4) return [];
      const grid = this.$refs.archiveGrid;
      if (!grid || !grid.querySelectorAll) return [];
      const itemEls = grid.querySelectorAll('.archive-grid-item');
      const selected = [];
      for (let i = 0; i < itemEls.length; i++) {
        const el = itemEls[i];
        const itemRect = el.getBoundingClientRect();
        const normalizedItemRect = {
          left: itemRect.left,
          top: itemRect.top,
          right: itemRect.right,
          bottom: itemRect.bottom,
        };
        if (this.archiveRectsIntersect(marqueeRect, normalizedItemRect)) {
          const type = el.getAttribute('data-archive-type');
          const id = el.getAttribute('data-archive-id');
          if (type && id) {
            selected.push({ type: type, id: String(id) });
          }
        }
      }
      return selected;
    },
    onDocumentClickClearArchiveSelection(event) {
      if (this.archiveSuppressNextSelectionClear) {
        this.archiveSuppressNextSelectionClear = false;
        return;
      }
      if (event && event.target && event.target.closest('.archive-context-menu')) return;
      if (event && event.target && event.target.closest('.archive-item-kebab-menu')) return;
      if (this.archiveContextMenu.visible) {
        this.closeArchiveContextMenu();
        return;
      }
      if (!this.archiveSelectedItems.length) return;
      if (event && event.target && event.target.closest('.archive-grid-item')) return;
      this.clearArchiveSelection();
    },
    onDocumentKeydownArchive(event) {
      if (!event) return;
      if (event.key === 'Escape') {
        if (this.archiveContextMenu.visible) {
          this.closeArchiveContextMenu();
        } else if (this.archiveClipboard && this.archiveClipboard.items && this.archiveClipboard.items.length) {
          this.clearArchiveClipboard();
        } else if (this.archiveSelectedItems.length) {
          this.clearArchiveSelection();
        }
        return;
      }
      const tag = event.target && event.target.tagName ? event.target.tagName.toLowerCase() : '';
      if (tag === 'input' || tag === 'textarea') return;
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (this.archiveSelectedItems.length && this.canDeleteArchiveSelection()) {
          event.preventDefault();
          this.deleteArchiveSelection();
        }
        return;
      }
      const isMod = event.ctrlKey || event.metaKey;
      if (!isMod) return;
      if (event.key === 'a' || event.key === 'A') {
        if (this.hasCurrentArchiveItems) {
          event.preventDefault();
          const ordered = this.getArchiveDisplayOrderItems();
          if (ordered.length > 0) {
            this.setArchiveSelection(ordered, ordered[0]);
          }
        }
        return;
      }
      if (event.key === 'c' || event.key === 'C') {
        if (this.archiveSelectedItems.length && this.canUploadToBoard) {
          event.preventDefault();
          this.copyArchiveSelection();
        }
        return;
      }
      if (event.key === 'x' || event.key === 'X') {
        if (this.archiveSelectedItems.length && this.canUploadToBoard) {
          event.preventDefault();
          this.cutArchiveSelection();
        }
        return;
      }
      if (event.key === 'v' || event.key === 'V') {
        if (this.canPasteArchiveClipboard()) {
          event.preventDefault();
          this.pasteArchiveClipboard();
        }
      }
    },
    openArchiveContextMenu(event, type, item) {
      if (!item || !item.id) return;
      let clientX = 0;
      let clientY = 0;
      if (event && typeof event.clientX === 'number') {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      this.openArchiveContextMenuAt(clientX, clientY, type, item);
    },
    openArchiveContextMenuAt(x, y, type, item) {
      if (!item || !item.id) return;
      this.prepareArchiveContextMenuSelection(type, item.id);
      const menuWidth = 190;
      const menuHeight = type === 'file' ? 260 : 220;
      const maxX = window.innerWidth - menuWidth - 8;
      const maxY = window.innerHeight - menuHeight - 8;
      const posX = Math.max(8, Math.min(x, maxX));
      const posY = Math.max(8, Math.min(y, maxY));
      this.archiveContextMenu = {
        visible: true,
        x: posX,
        y: posY,
        type: type,
        item: item,
      };
    },
    openArchiveItemKebabMenu(event, type, item) {
      if (!item || !item.id || !event) return;
      this.prepareArchiveContextMenuSelection(type, item.id);
      if (this.isArchiveContextMenuOpenForItem(type, item.id)) {
        this.closeArchiveContextMenu();
        return;
      }
      let clientX = event.clientX || 0;
      let clientY = event.clientY || 0;
      if (event.currentTarget && event.currentTarget.getBoundingClientRect) {
        const rect = event.currentTarget.getBoundingClientRect();
        clientX = rect.left;
        clientY = rect.bottom + 4;
      }
      this.openArchiveContextMenuAt(clientX, clientY, type, item);
    },
    isArchiveContextMenuOpenForItem(type, id) {
      const menu = this.archiveContextMenu;
      if (!menu.visible || !menu.item || !menu.item.id) return false;
      return menu.type === type && String(menu.item.id) === String(id);
    },
    openArchiveBackgroundContextMenu(event) {
      if (!this.canUploadToBoard && !this.canPasteArchiveClipboard()) return;
      let clientX = 0;
      let clientY = 0;
      if (event && typeof event.clientX === 'number') {
        clientX = event.clientX;
        clientY = event.clientY;
      }
      this.clearArchiveSelection();
      const menuWidth = 190;
      const menuHeight = (this.canUploadToBoard && this.canPasteArchiveClipboard()) ? 88 : 48;
      const maxX = window.innerWidth - menuWidth - 8;
      const maxY = window.innerHeight - menuHeight - 8;
      const posX = Math.max(8, Math.min(clientX, maxX));
      const posY = Math.max(8, Math.min(clientY, maxY));
      this.archiveContextMenu = {
        visible: true,
        x: posX,
        y: posY,
        type: 'background',
        item: null,
      };
    },
    openArchiveExplorerContextMenu(event) {
      if (event && event.target && event.target.closest('.archive-grid-item')) return;
      this.openArchiveBackgroundContextMenu(event);
    },
    closeArchiveContextMenu() {
      this.archiveContextMenu.visible = false;
      this.archiveContextMenu.type = null;
      this.archiveContextMenu.item = null;
    },
    executeArchiveContextMenuAction(actionId) {
      const menu = this.archiveContextMenu;
      if (!menu.visible) return;
      if (menu.type === 'background') {
        this.closeArchiveContextMenu();
        if (actionId === 'new-folder') {
          this.createAttachmentFolder();
        } else if (actionId === 'paste') {
          this.pasteArchiveClipboard();
        }
        return;
      }
      if (!menu.item) return;
      const item = menu.item;
      const type = menu.type;
      this.closeArchiveContextMenu();
      if (actionId === 'open') {
        if (type === 'folder') {
          this.openAttachmentFolder(item);
        } else {
          this.viewAttachment(item);
        }
        return;
      }
      if (actionId === 'download') {
        if (this.archiveSelectedItems.length > 1) {
          this.downloadArchiveSelection();
        } else if (type === 'folder') {
          this.downloadAttachmentFolder(item);
        } else if (type === 'file') {
          this.downloadAttachment(item);
        }
        return;
      }
      if (actionId === 'copy') {
        this.copyArchiveSelection();
        return;
      }
      if (actionId === 'cut') {
        this.cutArchiveSelection();
        return;
      }
      if (actionId === 'rename') {
        if (type === 'folder') {
          this.startInlineRenameAttachmentFolder(item);
        } else {
          this.startInlineRenameAttachment(item);
        }
        return;
      }
      if (actionId === 'delete') {
        this.deleteArchiveSelection();
      }
    },
    getSelectedItemsEligibleForDelete() {
      const selected = this.archiveSelectedItems;
      const folders = [];
      const files = [];
      for (let i = 0; i < selected.length; i++) {
        const sel = selected[i];
        const record = this.getArchiveItemRecord(sel.type, sel.id);
        if (!record) continue;
        if (sel.type === 'folder' && this.canEditAttachmentFolder(record)) {
          folders.push(record);
        } else if (sel.type === 'file' && this.canEditAttachment(record)) {
          files.push(record);
        }
      }
      const topLevelFolders = [];
      for (let j = 0; j < folders.length; j++) {
        const folder = folders[j];
        let isNestedInSelection = false;
        for (let k = 0; k < folders.length; k++) {
          if (String(folders[k].id) === String(folder.id)) continue;
          if (this.isFolderInsideTree(folder.id, folders[k].id)) {
            isNestedInSelection = true;
            break;
          }
        }
        if (!isNestedInSelection) {
          topLevelFolders.push(folder);
        }
      }
      const deletingFolderIds = {};
      for (let m = 0; m < topLevelFolders.length; m++) {
        deletingFolderIds[String(topLevelFolders[m].id)] = true;
      }
      const filesToDelete = [];
      for (let n = 0; n < files.length; n++) {
        const file = files[n];
        const parentId = file.folder_id ? String(file.folder_id) : null;
        if (parentId && deletingFolderIds[parentId]) continue;
        filesToDelete.push(file);
      }
      return {
        folders: topLevelFolders,
        files: filesToDelete,
      };
    },
    getDeletableSelectedCount() {
      const eligible = this.getSelectedItemsEligibleForDelete();
      return eligible.folders.length + eligible.files.length;
    },
    getSelectedItemsForDownload() {
      const selected = this.archiveSelectedItems;
      const folders = [];
      const files = [];
      for (let i = 0; i < selected.length; i++) {
        const sel = selected[i];
        const record = this.getArchiveItemRecord(sel.type, sel.id);
        if (!record) continue;
        if (sel.type === 'folder') {
          if (this.isAttachmentFolderDownloadable(record)) {
            folders.push(record);
          }
        } else if (sel.type === 'file') {
          files.push(record);
        }
      }
      const topLevelFolders = [];
      for (let j = 0; j < folders.length; j++) {
        const folder = folders[j];
        let isNestedInSelection = false;
        for (let k = 0; k < folders.length; k++) {
          if (String(folders[k].id) === String(folder.id)) continue;
          if (this.isFolderInsideTree(folder.id, folders[k].id)) {
            isNestedInSelection = true;
            break;
          }
        }
        if (!isNestedInSelection) {
          topLevelFolders.push(folder);
        }
      }
      const downloadingFolderIds = {};
      for (let m = 0; m < topLevelFolders.length; m++) {
        downloadingFolderIds[String(topLevelFolders[m].id)] = true;
      }
      const filesToDownload = [];
      for (let n = 0; n < files.length; n++) {
        const file = files[n];
        const parentId = file.folder_id ? String(file.folder_id) : null;
        if (parentId && downloadingFolderIds[parentId]) continue;
        filesToDownload.push(file);
      }
      return {
        folders: topLevelFolders,
        files: filesToDownload,
      };
    },
    getDownloadableSelectedCount() {
      const items = this.getSelectedItemsForDownload();
      return items.folders.length + items.files.length;
    },
    async downloadArchiveSelection() {
      const items = this.getSelectedItemsForDownload();
      const folders = items.folders;
      const files = items.files;
      const total = folders.length + files.length;
      if (total === 0) return;

      for (let i = 0; i < folders.length; i++) {
        await this.downloadAttachmentFolder(folders[i]);
        if (i < folders.length - 1 || files.length > 0) {
          await new Promise(function(resolve) { setTimeout(resolve, 300); });
        }
      }
      for (let j = 0; j < files.length; j++) {
        await this.downloadAttachment(files[j]);
        if (j < files.length - 1) {
          await new Promise(function(resolve) { setTimeout(resolve, 300); });
        }
      }
    },
    canDeleteArchiveSelection() {
      return this.getDeletableSelectedCount() > 0;
    },
    async deleteArchiveSelection() {
      const eligible = this.getSelectedItemsEligibleForDelete();
      const folders = eligible.folders;
      const files = eligible.files;
      const total = folders.length + files.length;
      if (total === 0) return;

      let confirmText = '';
      if (total === 1) {
        if (folders.length === 1) {
          const folderName = folders[0].name || 'this folder';
          confirmText = 'You are about to delete "' + folderName + '" and all files and subfolders inside it. This cannot be undone.';
        } else {
          const file = files[0];
          const fileName = file.display_name || file.original_filename || file.file_name || 'this file';
          confirmText = 'You are about to delete "' + fileName + '". This cannot be undone.';
        }
      } else {
        const parts = [];
        if (folders.length > 0) {
          parts.push(folders.length + ' ' + (folders.length === 1 ? 'folder' : 'folders'));
        }
        if (files.length > 0) {
          parts.push(files.length + ' ' + (files.length === 1 ? 'file' : 'files'));
        }
        confirmText = 'You are about to delete ' + parts.join(' and ') + '. This cannot be undone.';
        if (folders.length > 0) {
          confirmText += ' Selected folders and all their contents will be removed.';
        }
      }

      const result = await this.$swal.fire({
        title: total === 1 ? 'Delete item?' : 'Delete ' + total + ' items?',
        text: confirmText,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: total === 1 ? 'Yes, delete it' : 'Yes, delete them',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;

      let hadError = false;
      let errorMessage = '';
      try {
        for (let i = 0; i < folders.length; i++) {
          const folder = folders[i];
          const { data } = await this.$apollo.mutate({
            mutation: gql`
              mutation DeleteTaskBoardAttachmentFolder($id: ID!) {
                deleteTaskBoardAttachmentFolder(id: $id) {
                  error
                  error_code
                  message
                }
              }
            `,
            variables: { id: folder.id },
          });
          const payload = data && data.deleteTaskBoardAttachmentFolder;
          if (payload && payload.error) {
            hadError = true;
            errorMessage = payload.message || 'Failed to delete folder.';
            break;
          }
          if (this.currentAttachmentFolderId && this.isFolderInsideTree(this.currentAttachmentFolderId, folder.id)) {
            this.navigateAttachmentFolder(folder.parent_folder_id || null);
          }
        }
        if (!hadError) {
          for (let j = 0; j < files.length; j++) {
            const att = files[j];
            const { data } = await this.$apollo.mutate({
              mutation: gql`
                mutation DeleteTaskBoardAttachment($id: ID!) {
                  deleteTaskBoardAttachment(id: $id) {
                    error
                    error_code
                    message
                  }
                }
              `,
              variables: { id: att.id },
            });
            const payload = data && data.deleteTaskBoardAttachment;
            if (payload && payload.error) {
              hadError = true;
              errorMessage = payload.message || 'Failed to delete attachment.';
              break;
            }
          }
        }
        if (hadError) {
          alert(errorMessage);
        } else {
          this.cancelInlineRenameAttachmentFolder();
          this.cancelInlineRenameAttachment();
          this.clearArchiveSelection();
          await this.fetchBoard();
        }
      } catch (error) {
        console.error('Error deleting archive items:', error);
        alert('Failed to delete. Please try again.');
      }
    },
    setArchiveClipboard(mode) {
      if (!this.canUploadToBoard) return;
      const items = this.getSelectedArchiveItemsWithMeta();
      if (!items.length) return;
      this.archiveClipboard = {
        mode: mode,
        items: items,
        sourceFolderId: this.currentAttachmentFolderId || null,
      };
    },
    copyArchiveSelection() {
      this.setArchiveClipboard('copy');
    },
    cutArchiveSelection() {
      this.setArchiveClipboard('cut');
    },
    clearArchiveClipboard() {
      this.archiveClipboard = {
        mode: null,
        items: [],
        sourceFolderId: null,
      };
    },
    isArchiveItemCut(type, id) {
      const clip = this.archiveClipboard;
      if (!clip || clip.mode !== 'cut' || !clip.items || !clip.items.length) return false;
      const key = this.getArchiveSelectionKey(type, id);
      for (let i = 0; i < clip.items.length; i++) {
        if (this.getArchiveSelectionKey(clip.items[i].type, clip.items[i].id) === key) {
          return true;
        }
      }
      return false;
    },
    isSameArchiveFolderId(folderIdA, folderIdB) {
      const a = folderIdA ? String(folderIdA) : null;
      const b = folderIdB ? String(folderIdB) : null;
      return a === b;
    },
    canPasteArchiveClipboard() {
      const clip = this.archiveClipboard;
      if (!this.canUploadToBoard || !clip.items || !clip.items.length) return false;
      const destinationFolderId = this.currentAttachmentFolderId || null;
      if (clip.mode === 'cut') {
        let allSameLocation = true;
        for (let i = 0; i < clip.items.length; i++) {
          const item = clip.items[i];
          const record = this.getArchiveItemRecord(item.type, item.id);
          if (!record) continue;
          const itemParent = this.getArchiveItemSourceFolderId(item.type, record);
          if (!this.isSameArchiveFolderId(itemParent, destinationFolderId)) {
            allSameLocation = false;
            break;
          }
        }
        if (allSameLocation) return false;
      }
      for (let j = 0; j < clip.items.length; j++) {
        const folderItem = clip.items[j];
        if (folderItem.type === 'folder') {
          const clipId = folderItem.id;
          if (this.currentAttachmentFolderId && String(this.currentAttachmentFolderId) === String(clipId)) {
            return false;
          }
          if (this.currentAttachmentFolderId && this.isFolderInsideTree(this.currentAttachmentFolderId, clipId)) {
            return false;
          }
        }
      }
      return true;
    },
    async pasteArchiveClipboard() {
      if (!this.canPasteArchiveClipboard() || this.archivePasting) return;
      const clip = this.archiveClipboard;
      const destinationFolderId = this.currentAttachmentFolderId;
      const isCut = clip.mode === 'cut';
      const items = clip.items.slice();
      const folders = [];
      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type === 'folder') {
          folders.push(items[i]);
        } else if (items[i].type === 'file') {
          files.push(items[i]);
        }
      }
      const orderedItems = folders.concat(files);
      this.archivePasting = true;
      let hadError = false;
      let errorMessage = '';
      try {
        for (let j = 0; j < orderedItems.length; j++) {
          const item = orderedItems[j];
          if (item.type === 'file') {
            const mutation = isCut
              ? gql`
                mutation MoveTaskBoardAttachment($id: ID!, $destination_folder_id: ID) {
                  moveTaskBoardAttachment(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `
              : gql`
                mutation CopyTaskBoardAttachment($id: ID!, $destination_folder_id: ID) {
                  copyTaskBoardAttachment(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `;
            const { data } = await this.$apollo.mutate({
              mutation: mutation,
              variables: {
                id: item.id,
                destination_folder_id: destinationFolderId,
              },
            });
            const payloadKey = isCut ? 'moveTaskBoardAttachment' : 'copyTaskBoardAttachment';
            const payload = data && data[payloadKey];
            if (payload && payload.error) {
              hadError = true;
              errorMessage = payload.message || (isCut ? 'Failed to move file.' : 'Failed to paste file.');
              break;
            }
          } else if (item.type === 'folder') {
            const mutation = isCut
              ? gql`
                mutation MoveTaskBoardAttachmentFolder($id: ID!, $destination_folder_id: ID) {
                  moveTaskBoardAttachmentFolder(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `
              : gql`
                mutation CopyTaskBoardAttachmentFolder($id: ID!, $destination_folder_id: ID) {
                  copyTaskBoardAttachmentFolder(id: $id, destination_folder_id: $destination_folder_id) {
                    error
                    error_code
                    message
                  }
                }
              `;
            const { data } = await this.$apollo.mutate({
              mutation: mutation,
              variables: {
                id: item.id,
                destination_folder_id: destinationFolderId,
              },
            });
            const payloadKey = isCut ? 'moveTaskBoardAttachmentFolder' : 'copyTaskBoardAttachmentFolder';
            const payload = data && data[payloadKey];
            if (payload && payload.error) {
              hadError = true;
              errorMessage = payload.message || (isCut ? 'Failed to move folder.' : 'Failed to paste folder.');
              break;
            }
          }
        }
        if (hadError) {
          alert(errorMessage);
        } else {
          if (isCut) {
            this.clearArchiveClipboard();
            this.clearArchiveSelection();
          }
          await this.fetchBoard();
        }
      } catch (error) {
        console.error('Error pasting archive items:', error);
        alert(isCut ? 'Failed to move. Please try again.' : 'Failed to paste. Please try again.');
      } finally {
        this.archivePasting = false;
      }
    },
    getArchiveItemSourceFolderId(type, item) {
      if (!item) return null;
      if (type === 'folder') {
        return item.parent_folder_id || null;
      }
      if (type === 'file') {
        return item.folder_id || null;
      }
      return null;
    },
    startInlineRenameAttachmentFolder(folder) {
      if (!folder || !folder.id) return;
      if (!this.canEditAttachmentFolder(folder)) return;
      this.inlineRenamingFolderId = folder.id;
      this.inlineRenamingFolderName = folder.name || '';
      this.$nextTick(() => {
        const refKey = 'folderRenameInput-' + folder.id;
        const el = this.$refs[refKey];
        const input = Array.isArray(el) ? el[0] : el;
        if (input && input.focus) {
          input.focus();
          if (input.select) {
            input.select();
          }
        }
      });
    },
    cancelInlineRenameAttachmentFolder() {
      this.inlineRenamingFolderId = null;
      this.inlineRenamingFolderName = '';
      this.inlineRenamingFolderSaving = false;
    },
    async commitInlineRenameAttachmentFolder(folder) {
      if (this.inlineRenamingFolderSaving) return;
      if (!folder || !folder.id) {
        this.cancelInlineRenameAttachmentFolder();
        return;
      }
      if (!this.inlineRenamingFolderId || String(this.inlineRenamingFolderId) !== String(folder.id)) {
        return;
      }
      const nextName = String(this.inlineRenamingFolderName || '').trim();
      const currentName = String(folder.name || '').trim();
      if (nextName === '') {
        this.inlineRenamingFolderName = currentName;
        this.cancelInlineRenameAttachmentFolder();
        return;
      }
      if (nextName === currentName) {
        this.cancelInlineRenameAttachmentFolder();
        return;
      }
      this.inlineRenamingFolderSaving = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTaskBoardAttachmentFolder($id: ID!, $name: String!) {
              updateTaskBoardAttachmentFolder(id: $id, name: $name) {
                error
                error_code
                message
                data { id name }
              }
            }
          `,
          variables: { id: folder.id, name: nextName },
        });
        const payload = data && data.updateTaskBoardAttachmentFolder;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to rename folder.');
          this.inlineRenamingFolderName = currentName;
          return;
        }
        await this.fetchBoard();
        this.cancelInlineRenameAttachmentFolder();
      } catch (error) {
        console.error('Error renaming folder:', error);
        alert('Failed to rename folder. Please try again.');
        this.inlineRenamingFolderName = currentName;
      } finally {
        this.inlineRenamingFolderSaving = false;
      }
    },
    startInlineRenameAttachment(att) {
      if (!att || !att.id) return;
      if (!this.canEditAttachment(att)) return;
      this.inlineRenamingAttachmentId = att.id;
      this.inlineRenamingAttachmentName = this.getAttachmentDisplayName(att);
      this.$nextTick(() => {
        const refKey = 'attachmentRenameInput-' + att.id;
        const el = this.$refs[refKey];
        const input = Array.isArray(el) ? el[0] : el;
        if (input && input.focus) {
          input.focus();
          if (input.select) {
            input.select();
          }
        }
      });
    },
    cancelInlineRenameAttachment() {
      this.inlineRenamingAttachmentId = null;
      this.inlineRenamingAttachmentName = '';
      this.inlineRenamingAttachmentSaving = false;
    },
    async commitInlineRenameAttachment(att) {
      if (this.inlineRenamingAttachmentSaving) return;
      if (!att || !att.id) {
        this.cancelInlineRenameAttachment();
        return;
      }
      if (!this.inlineRenamingAttachmentId || String(this.inlineRenamingAttachmentId) !== String(att.id)) {
        return;
      }
      const nextName = String(this.inlineRenamingAttachmentName || '').trim();
      const currentName = String(this.getAttachmentDisplayName(att) || '').trim();
      if (nextName === '') {
        this.inlineRenamingAttachmentName = currentName;
        this.cancelInlineRenameAttachment();
        return;
      }
      if (nextName === currentName) {
        this.cancelInlineRenameAttachment();
        return;
      }
      this.inlineRenamingAttachmentSaving = true;
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTaskBoardAttachment($id: ID!, $display_name: String!) {
              updateTaskBoardAttachment(id: $id, display_name: $display_name) {
                error
                error_code
                message
                data { id display_name }
              }
            }
          `,
          variables: { id: att.id, display_name: nextName },
        });
        const payload = data && data.updateTaskBoardAttachment;
        if (payload && payload.error) {
          alert(payload.message || 'Failed to update attachment.');
          this.inlineRenamingAttachmentName = currentName;
          return;
        }
        await this.fetchBoard();
        this.cancelInlineRenameAttachment();
      } catch (error) {
        console.error('Error updating attachment:', error);
        alert('Failed to update attachment. Please try again.');
        this.inlineRenamingAttachmentName = currentName;
      } finally {
        this.inlineRenamingAttachmentSaving = false;
      }
    },
    isAttachmentFolderEmpty(folderId) {
      if (!folderId) return true;
      const idStr = String(folderId);
      const attachments = this.attachments;
      for (let i = 0; i < attachments.length; i++) {
        const att = attachments[i];
        if (att.folder_id && String(att.folder_id) === idStr) {
          return false;
        }
      }
      const folders = this.attachmentFolders;
      for (let j = 0; j < folders.length; j++) {
        const child = folders[j];
        if (child.parent_folder_id && String(child.parent_folder_id) === idStr) {
          if (!this.isAttachmentFolderEmpty(child.id)) {
            return false;
          }
        }
      }
      return true;
    },
    isAttachmentFolderDownloadable(folder) {
      if (!folder || !folder.id) return false;
      return !this.isAttachmentFolderEmpty(folder.id);
    },
    isFolderInsideTree(folderId, ancestorFolderId) {
      if (!folderId || !ancestorFolderId) return false;
      if (String(folderId) === String(ancestorFolderId)) return true;
      let current = null;
      const folders = this.attachmentFolders;
      for (let i = 0; i < folders.length; i++) {
        if (String(folders[i].id) === String(folderId)) {
          current = folders[i];
          break;
        }
      }
      if (!current || !current.parent_folder_id) return false;
      return this.isFolderInsideTree(current.parent_folder_id, ancestorFolderId);
    },
    checkCanMoveTask(evt) {
      const task = evt && evt.draggedContext && evt.draggedContext.element ? evt.draggedContext.element : null;
      if (!task || !this.currentUserId) return false;
      const isCreator = task.created_by && task.created_by.id && parseInt(task.created_by.id) === parseInt(this.currentUserId);
      const isAssignee = task.assignees && task.assignees.some(a => parseInt(a.id) === parseInt(this.currentUserId));
      const isViewer = task.viewers && task.viewers.some(v => parseInt(v.id) === parseInt(this.currentUserId));
      return isCreator || isAssignee || isViewer;
    },
    handleTaskClick(taskId) {
      // Prevent opening modal while drag is happening (common UX bug)
      if (this.isDragging) {
        return;
      }
      this.openTaskDetail(taskId);
    },
    async onTaskDragChange(evt, destinationColumn) {
      // Trigger only on destination list when an element is added
      if (!evt) return;

      // Cross-column move
      const added = evt.added && evt.added.element ? evt.added : null;
      // Same-column reorder
      const moved = evt.moved && evt.moved.element ? evt.moved : null;

      const change = added || moved;
      if (!change) return;

      const movedTask = change.element;
      const destColumnId = parseInt(destinationColumn.id);
      const newIndex = typeof change.newIndex === 'number' ? change.newIndex : null;

      if (!movedTask || !destColumnId) return;

      try {
        this.isDragging = true;
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation MoveTask($id: ID!, $column_id: ID!, $order: Int) {
              moveTask(id: $id, column_id: $column_id, order: $order) {
                error
                error_code
                message
                data { id status column_id }
              }
            }
          `,
          variables: {
            id: movedTask.id,
            column_id: destColumnId,
            order: newIndex,
          },
        });

        if (data && data.moveTask && data.moveTask.error && this.$swal) {
          await this.$swal.fire({ icon: 'error', title: 'Cannot move task', text: data.moveTask.message || 'You do not have permission to move this task.' });
        }

        // Refresh tasks from server so UI matches persisted state
        this.refreshTasks();
      } catch (e) {
        // If backend update fails, revert UI by refetching
        this.refreshTasks();
        if (this.$swal) {
          await this.$swal.fire({ icon: 'error', title: 'Error', text: (e && e.message) || 'Failed to move task.' });
        }
      } finally {
        this.isDragging = false;
      }
    },
    getPriorityColor(priority) {
      const colors = {
        low: '#10B981',
        medium: '#499DD4',
        high: '#F59E0B',
        critical: '#EF4444',
      };
      return colors[priority] || '#9BA4B5';
    },
    getInitials(name) {
      if (!name) return '?';
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    getStatusLabel(status) {
      const labels = {
        todo: 'To Do',
        in_progress: 'In Progress',
        paused: 'Paused',
        in_review: 'In Review',
        done: 'Done',
      };
      return labels[(status || 'todo').toLowerCase()] || status;
    },
    getStatusBadgeClass(status) {
      const s = (status || 'todo').toLowerCase();
      const classes = {
        todo: 'bg-gray-100 text-gray-700',
        in_progress: 'bg-blue-100 text-blue-700',
        paused: 'bg-amber-100 text-amber-700',
        in_review: 'bg-purple-100 text-purple-700',
        done: 'bg-green-100 text-green-700',
      };
      return classes[s] || 'bg-gray-100 text-gray-700';
    },
    formatMinutes(minutes) {
      if (!minutes || minutes < 0) return '0m';
      if (minutes < 60) return minutes + 'm';
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      return m > 0 ? h + 'h ' + m + 'm' : h + 'h';
    },
    formatEstimatedDisplay(minutes, type) {
      if (!minutes || minutes < 0) return '0m';
      if (type === 'days') return (minutes / 1440 === 1 ? '1 day' : Math.round(minutes / 1440) + ' days');
      return this.formatMinutes(minutes);
    },
    formatTimeSpent(seconds) {
      if (seconds == null || seconds < 0) return '0s';
      const s = Math.floor(seconds);
      if (s < 60) return s + 's';
      const m = Math.floor(s / 60);
      const secs = s % 60;
      if (m < 60) return secs > 0 ? m + 'm ' + secs + 's' : m + 'm';
      const h = Math.floor(m / 60);
      const mins = m % 60;
      return mins > 0 || secs > 0 ? h + 'h ' + mins + 'm ' + secs + 's' : h + 'h';
    },
    getProgressBarColorClass(percent) {
      if (percent == null) return 'bg-blue-500';
      if (percent > 100) return 'bg-red-500';
      if (percent >= 90) return 'bg-amber-500';
      return 'bg-blue-500';
    },
    openTaskDetail(taskId) {
      this.selectedTaskId = taskId;
    },
    async refreshTasks() {
      this.fetchStatistics();
      if (this.taskViewMode === 'grid') {
        await this.fetchBoardTasksList(1);
      } else if (this.taskViewMode === 'calendar') {
        await this.fetchBoardCalendarTasks();
      } else {
        await this.fetchTasksForAllColumns();
      }
    },
    setTaskViewMode(mode) {
      if (mode !== 'board' && mode !== 'grid' && mode !== 'calendar') {
        return;
      }
      if (this.taskViewMode === mode) {
        return;
      }
      this.taskViewMode = mode;
      if (mode === 'grid') {
        this.fetchBoardTasksList(1);
      } else if (mode === 'calendar') {
        this.fetchBoardCalendarTasks();
      } else {
        this.refreshTasks();
      }
    },
    setListTasksPage(page) {
      var nextPage = parseInt(page, 10);
      if (!nextPage || nextPage < 1) {
        return;
      }
      this.fetchBoardTasksList(nextPage, true);
    },
    scrollBoardGridToTop() {
      var self = this;
      this.$nextTick(function () {
        if (self.taskViewMode !== 'grid') {
          return;
        }
        var el = self.$refs.boardListGridAnchor;
        if (!el || !el.scrollIntoView) {
          return;
        }
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    },
    async fetchBoardTasksList(page, scrollToTop) {
      if (!this.boardId) {
        return;
      }
      if (this.activeTab === 'my' && !this.currentUserId) {
        return;
      }
      if (this.activeTab === 'viewer' && !this.currentUserId) {
        return;
      }

      this.listTasksFetchBatchId = (this.listTasksFetchBatchId || 0) + 1;
      var batchId = this.listTasksFetchBatchId;
      this.listTasksLoading = true;

      if (scrollToTop) {
        this.scrollBoardGridToTop();
      }

      var searchTrim = (this.boardTaskSearch || '').trim();
      var searchVar = searchTrim.length > 0 ? searchTrim : null;
      var assigneeId = null;
      var viewerId = null;
      if (this.activeTab === 'my') {
        assigneeId = String(this.currentUserId);
      } else if (this.activeTab === 'viewer') {
        viewerId = String(this.currentUserId);
      }

      try {
        var result = await this.$apollo.query({
          query: gql`
            query GetBoardTasksList($board_id: ID!, $assignee_id: ID, $viewer_id: ID, $search: String, $first: Int!, $page: Int!) {
              boardTasksList(
                board_id: $board_id
                assignee_id: $assignee_id
                viewer_id: $viewer_id
                search: $search
                first: $first
                page: $page
              ) {
                data {
                  id
                  title
                  description
                  priority
                  status
                  due_date
                  created_at
                  column { id name color }
                  assignees { id name }
                  created_by { id name }
                }
                paginatorInfo {
                  count
                  currentPage
                  firstItem
                  hasMorePages
                  lastItem
                  lastPage
                  perPage
                  total
                }
              }
            }
          `,
          variables: {
            board_id: String(this.boardId),
            assignee_id: assigneeId,
            viewer_id: viewerId,
            search: searchVar,
            first: 10,
            page: page || 1,
          },
          fetchPolicy: 'network-only',
        });

        if (batchId !== this.listTasksFetchBatchId) {
          return;
        }

        var payload = result.data && result.data.boardTasksList;
        this.listTasks = payload && payload.data ? payload.data : [];
        this.listPaginatorInfo = payload && payload.paginatorInfo ? payload.paginatorInfo : null;
        if (scrollToTop) {
          this.scrollBoardGridToTop();
        }
      } catch (error) {
        console.error('Error fetching board tasks list:', error);
        if (batchId === this.listTasksFetchBatchId) {
          this.listTasks = [];
          this.listPaginatorInfo = null;
        }
      } finally {
        if (batchId === this.listTasksFetchBatchId) {
          this.listTasksLoading = false;
        }
      }
    },
    async fetchBoardCalendarTasks() {
      if (!this.boardId) {
        return;
      }
      if (this.activeTab === 'my' && !this.currentUserId) {
        return;
      }
      if (this.activeTab === 'viewer' && !this.currentUserId) {
        return;
      }

      this.calendarTasksFetchBatchId = (this.calendarTasksFetchBatchId || 0) + 1;
      var batchId = this.calendarTasksFetchBatchId;
      this.calendarTasksLoading = true;

      var searchTrim = (this.boardTaskSearch || '').trim();
      var searchVar = searchTrim.length > 0 ? searchTrim : null;
      var assigneeId = null;
      var viewerId = null;
      if (this.activeTab === 'my') {
        assigneeId = String(this.currentUserId);
      } else if (this.activeTab === 'viewer') {
        viewerId = String(this.currentUserId);
      }

      try {
        var allTasks = [];
        var page = 1;
        var lastPage = 1;

        do {
          var result = await this.$apollo.query({
            query: gql`
              query GetBoardTasksListCalendar($board_id: ID!, $assignee_id: ID, $viewer_id: ID, $search: String, $first: Int!, $page: Int!) {
                boardTasksList(
                  board_id: $board_id
                  assignee_id: $assignee_id
                  viewer_id: $viewer_id
                  search: $search
                  first: $first
                  page: $page
                ) {
                  data {
                    id
                    title
                    description
                    priority
                    status
                    due_date
                    created_at
                    column { id name color }
                    assignees { id name }
                    created_by { id name }
                  }
                  paginatorInfo {
                    currentPage
                    lastPage
                  }
                }
              }
            `,
            variables: {
              board_id: String(this.boardId),
              assignee_id: assigneeId,
              viewer_id: viewerId,
              search: searchVar,
              first: 100,
              page: page,
            },
            fetchPolicy: 'network-only',
          });

          if (batchId !== this.calendarTasksFetchBatchId) {
            return;
          }

          var payload = result.data && result.data.boardTasksList;
          var chunk = payload && payload.data ? payload.data : [];
          for (var i = 0; i < chunk.length; i++) {
            allTasks.push(chunk[i]);
          }

          var info = payload && payload.paginatorInfo ? payload.paginatorInfo : null;
          lastPage = info && info.lastPage ? info.lastPage : 1;
          page = page + 1;
        } while (page <= lastPage);

        if (batchId === this.calendarTasksFetchBatchId) {
          this.calendarTasks = allTasks;
        }
      } catch (error) {
        console.error('Error fetching board calendar tasks:', error);
        if (batchId === this.calendarTasksFetchBatchId) {
          this.calendarTasks = [];
        }
      } finally {
        if (batchId === this.calendarTasksFetchBatchId) {
          this.calendarTasksLoading = false;
        }
      }
    },
    listTaskNumber(index) {
      var i = parseInt(index, 10);
      if (isNaN(i) || i < 0) {
        i = 0;
      }
      if (this.listPaginatorInfo && this.listPaginatorInfo.firstItem) {
        return this.listPaginatorInfo.firstItem + i;
      }
      var page = this.listPaginatorInfo && this.listPaginatorInfo.currentPage ? this.listPaginatorInfo.currentPage : 1;
      return (page - 1) * 10 + i + 1;
    },
    formatAssigneesList(assignees) {
      if (!assignees || !assignees.length) {
        return 'Unassigned';
      }
      return assignees.map(function (a) {
        return a.name || 'Unknown';
      }).join(', ');
    },
    formatPriorityLabel(priority) {
      if (!priority) {
        return 'Medium';
      }
      var p = String(priority).toLowerCase();
      return p.charAt(0).toUpperCase() + p.slice(1);
    },
    formatCreatedAt(dateStr) {
      if (!dateStr) {
        return '—';
      }
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) {
        return dateStr;
      }
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatDueDateDisplay(dateStr) {
      if (!dateStr) {
        return '—';
      }
      return this.formatDate(dateStr);
    },
    isTaskDueLate(task) {
      if (!task || !task.due_date) {
        return false;
      }
      var status = (task.status || '').toLowerCase();
      if (status === 'done' || status === 'in_review') {
        return false;
      }
      var due = new Date(task.due_date);
      if (isNaN(due.getTime())) {
        return false;
      }
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      due.setHours(0, 0, 0, 0);
      return due < today;
    },
    getBucketColor(column) {
      if (column && column.color) {
        return column.color;
      }
      return '#9BA4B5';
    },
    async fetchBoard() {
      if (!this.boardId) {
        return;
      }

      this.boardLoading = true;
      try {
        const result = await this.$apollo.query({
          query: gql`
            query GetTaskBoardStructure($id: ID!) {
              taskBoardStructure(id: $id) {
                error
                error_code
                message
                data {
                  id
                  token
                  name
                  description
                  color
                  master_board {
                    id
                    name
                  }
                  attachments {
                    id
                    folder_id
                    file_name
                    original_filename
                    display_name
                    file_type
                    file_size
                    uploaded_by { id name }
                  }
                  attachment_folders {
                    id
                    name
                    parent_folder_id
                    created_at
                    created_by { id name }
                  }
                  columns {
                    id
                    name
                    description
                    order
                    color
                  }
                }
              }
            }
          `,
          variables: {
            id: this.boardId,
          },
          fetchPolicy: 'network-only',
        });

        if (result.data && result.data.taskBoardStructure) {
          this.board = result.data.taskBoardStructure;
          this.updateLocalColumns(this.boardData);
          await this.fetchBoardMembers();
          if (this.taskViewMode === 'grid') {
            await this.fetchBoardTasksList(1);
          } else if (this.taskViewMode === 'calendar') {
            await this.fetchBoardCalendarTasks();
          } else {
            await this.fetchTasksForAllColumns();
          }
        }
      } catch (error) {
        console.error('Error fetching board:', error);
        this.board = null;
      } finally {
        this.boardLoading = false;
      }
    },
    async fetchBoardMembers() {
      if (!this.boardId) return;
      this.boardMembersLoading = true;
      try {
        const result = await this.$apollo.query({
          query: gql`
            query GetTaskBoardMembers($board_id: ID!) {
              taskBoardMembers(board_id: $board_id) {
                error
                error_code
                message
                data {
                  members {
                    id
                    token
                    name
                    email
                    profile_picture_url
                    total_tasks
                    todo_count
                    in_progress_count
                    paused_count
                    in_review_count
                    done_count
                  }
                  creator_id
                }
              }
            }
          `,
          variables: {
            board_id: this.boardId,
          },
          fetchPolicy: 'network-only',
        });
        const payload = result.data && result.data.taskBoardMembers;
        if (payload && !payload.error && payload.data) {
          this.boardMembersData = payload.data.members || [];
          this.boardCreatorId = payload.data.creator_id || null;
        } else {
          this.boardMembersData = [];
          this.boardCreatorId = null;
        }
      } catch (e) {
        console.error('Error fetching board members:', e);
        this.boardMembersData = [];
        this.boardCreatorId = null;
      } finally {
        this.boardMembersLoading = false;
      }
    },
    async fetchStatistics() {
      if (!this.boardId) {
        return;
      }

      this.statisticsLoading = true;
      try {
        const result = await this.$apollo.query({
          query: gql`
            query GetTaskBoardStatistics($id: ID!, $filterMyTasks: Boolean, $filterViewerTasks: Boolean) {
              taskBoardStatistics(id: $id, filter_my_tasks: $filterMyTasks, filter_viewer_tasks: $filterViewerTasks) {
                error
                error_code
                message
                data {
                  total
                  todo
                  in_progress
                  paused
                  in_review
                  done
                  priority_stats {
                    low
                    medium
                    high
                    critical
                  }
                  status_stats {
                    todo
                    in_progress
                    paused
                    in_review
                    done
                  }
                  bucket_stats {
                    name
                    count
                  }
                }
              }
            }
          `,
          variables: {
            id: this.boardId,
            filterMyTasks: this.activeTab === 'my',
            filterViewerTasks: this.activeTab === 'viewer',
          },
          fetchPolicy: 'network-only',
        });

        if (result.data && result.data.taskBoardStatistics) {
          const payload = result.data.taskBoardStatistics;
          if (!payload.error && payload.data) {
            this.statistics = payload.data;
          } else {
            this.statistics = null;
          }
        }
      } catch (error) {
        console.error('Error fetching statistics:', error);
        this.statistics = null;
      } finally {
        this.statisticsLoading = false;
      }
    },
    openExportModal() {
      if (!this.boardData) {
        return;
      }
      this.showExportModal = true;
    },
    closeExportModal() {
      this.showExportModal = false;
    },
    handleTaskCreated() {
      this.showCreateTaskModal = false;
      this.refreshTasks();
    },
    handleTaskUpdated() {
      this.refreshTasks();
    },
    handleTaskDeleted() {
      this.selectedTaskId = null;
      this.refreshTasks();
    },
    closeAddBucketModal() {
      this.showAddBucketModal = false;
      this.newBucketName = '';
      this.addBucketError = null;
    },
    async createBucket() {
      const name = (this.newBucketName || '').trim();
      if (!name || !this.boardId) return;
      this.addBucketLoading = true;
      this.addBucketError = null;
      try {
        const result = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateTaskColumn($input: CreateTaskColumnInput!) {
              createTaskColumn(input: $input) {
                error
                error_code
                message
                data {
                  id
                  name
                  description
                  order
                  color
                }
              }
            }
          `,
          variables: {
            input: {
              board_id: this.boardId,
              name: name,
              order: (this.columns && this.columns.length) ? this.columns.length : 0,
            },
          },
        });
        const payload = result.data && result.data.createTaskColumn;
        if (payload && !payload.error && payload.data) {
          this.closeAddBucketModal();
          await this.fetchBoard();
          await this.fetchStatistics();
        } else {
          this.addBucketError = (payload && payload.message) ? payload.message : 'Failed to create bucket';
        }
      } catch (error) {
        console.error('Error creating bucket:', error);
        this.addBucketError = (error && error.message) ? error.message : 'Failed to create bucket';
      } finally {
        this.addBucketLoading = false;
      }
    },
    startEditBucket(column) {
      this.editingColumnId = column.id;
      this.editBucketName = column.name || '';
      this.editBucketLoading = false;
      this.editBucketError = null;
      this.$nextTick(function () {
        const ref = this.$refs.editBucketInput;
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (el && el.focus) el.focus();
      }.bind(this));
    },
    cancelEditBucket() {
      this.editingColumnId = null;
      this.editBucketName = '';
      this.editBucketLoading = false;
      this.editBucketError = null;
    },
    openQuickAddTask(column) {
      this.addingColumnId = column.id;
      this.quickAddTitle = '';
      this.quickAddDueDate = '';
      const self = this.boardMembers.find(function (m) {
        return m.id && this.currentUserId && parseInt(m.id) === parseInt(this.currentUserId);
      }.bind(this));
      this.quickAddAssignees = self ? [self] : [];
      this.quickAddLoading = false;
    },
    closeQuickAddTask() {
      this.addingColumnId = null;
      this.quickAddTitle = '';
      this.quickAddDueDate = '';
      this.quickAddAssignees = [];
      this.quickAddLoading = false;
    },
    async submitQuickAddTask(column) {
      const title = (this.quickAddTitle || '').trim();
      if (!title || !this.quickAddAssignees || this.quickAddAssignees.length === 0 || !column.id || !this.boardId) return;
      this.quickAddLoading = true;
      try {
        const input = {
          board_id: parseInt(this.boardId),
          column_id: parseInt(column.id),
          title: title,
          description: null,
          status: 'todo',
          priority: 'medium',
          assignee_ids: this.quickAddAssignees.map(function (m) {
            return parseInt(m.id, 10);
          }),
        };
        if (this.quickAddDueDate) {
          input.due_date = this.quickAddDueDate;
        }
        const result = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateTask($input: CreateTaskInput!) {
              createTask(input: $input) {
                error
                error_code
                message
                data { id title }
              }
            }
          `,
          variables: { input },
        });
        const payload = result.data && result.data.createTask;
        if (payload && !payload.error && payload.data) {
          this.closeQuickAddTask();
          this.refreshTasks();
        } else {
          const msg = (payload && payload.message) ? payload.message : 'Failed to create task';
          if (this.$swal && this.$swal.fire) this.$swal.fire({ icon: 'error', title: 'Error', text: msg });
        }
      } catch (error) {
        console.error('Error creating task:', error);
        if (this.$swal && this.$swal.fire) this.$swal.fire({ icon: 'error', title: 'Error', text: 'Failed to create task.' });
      } finally {
        this.quickAddLoading = false;
      }
    },
    async saveBucketName(column) {
      const name = (this.editBucketName || '').trim();
      if (!name || !column.id) return;
      this.editBucketLoading = true;
      this.editBucketError = null;
      try {
        const result = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateTaskColumn($id: ID!, $input: UpdateTaskColumnInput!) {
              updateTaskColumn(id: $id, input: $input) {
                error
                error_code
                message
                data {
                  id
                  name
                  description
                  order
                  color
                }
              }
            }
          `,
          variables: {
            id: column.id,
            input: { name: name },
          },
        });
        const payload = result.data && result.data.updateTaskColumn;
        if (payload && !payload.error && payload.data) {
          this.cancelEditBucket();
          const col = this.localColumns.find(function (c) { return String(c.id) === String(column.id); });
          if (col) col.name = payload.data.name;
          if (this.boardData && this.boardData.columns) {
            const boardCol = this.boardData.columns.find(function (c) { return String(c.id) === String(column.id); });
            if (boardCol) boardCol.name = payload.data.name;
          }
        } else {
          this.editBucketError = (payload && payload.message) ? payload.message : 'Failed to update bucket name';
        }
      } catch (error) {
        console.error('Error updating bucket:', error);
        this.editBucketError = (error && error.message) ? error.message : 'Failed to update bucket name';
      } finally {
        this.editBucketLoading = false;
      }
    },
    setActiveTab(tab) {
      this.activeTab = tab;
    },
    getProfilePicture(url) {
      if (!url) {
        return '/img/default-avatar.png';
      }
      if (url.indexOf('http') === 0) {
        return url;
      }
      return process.env.VUE_APP_PUBLIC_URL + url;
    },
    handleImageError(event) {
      event.target.src = '/img/default-avatar.png';
    },
    isCreator(member) {
      if (!member || !this.boardCreatorId) return false;
      return parseInt(member.id) === parseInt(this.boardCreatorId);
    },
    getViewerMemberTaskCount(memberId) {
      let count = 0;
      const cols = this.boardData && this.boardData.columns ? this.boardData.columns : [];
      for (let i = 0; i < cols.length; i++) {
        const cid = String(cols[i].id);
        const data = this.viewerTasksData[cid];
        const tasks = data && data.tasks ? data.tasks : [];
        for (let j = 0; j < tasks.length; j++) {
          const task = tasks[j];
          const assignees = task.assignees || [];
          if (assignees.some(function (a) { return a && parseInt(a.id) === parseInt(memberId); })) {
            count++;
          }
        }
      }
      return count;
    },
    getMemberTaskStats(member) {
      if (!member || !member.id) {
        return { total: 0, todo: 0, in_progress: 0, paused: 0, in_review: 0, done: 0 };
      }
      if (member.total_tasks !== undefined) {
        return {
          total: member.total_tasks || 0,
          todo: member.todo_count || 0,
          in_progress: member.in_progress_count || 0,
          paused: member.paused_count || 0,
          in_review: member.in_review_count || 0,
          done: member.done_count || 0,
        };
      }
      const stats = this.memberTaskStats[parseInt(member.id)];
      return stats || { total: 0, todo: 0, in_progress: 0, paused: 0, in_review: 0, done: 0 };
    },
    goToProfile(member) {
      if (member && member.token) {
        this.$router.push(`/profile/${member.token}`);
      }
    },
    openMemberTasksModal(member) {
      this.selectedMemberForTasks = member;
    },
    closeMemberTasksModal() {
      this.selectedMemberForTasks = null;
    },
    scheduleBoardTaskSearchRefetch() {
      if (this.boardSearchDebounceTimer) {
        clearTimeout(this.boardSearchDebounceTimer);
        this.boardSearchDebounceTimer = null;
      }
      var self = this;
      this.boardSearchDebounceTimer = setTimeout(function () {
        self.boardSearchDebounceTimer = null;
        if (!self.boardId || !self.boardData) return;
        if (self.taskViewMode === 'grid') {
          self.fetchBoardTasksList(1);
        } else if (self.taskViewMode === 'calendar') {
          self.fetchBoardCalendarTasks();
        } else {
          self.fetchTasksForAllColumns();
        }
      }, 400);
    },
    getColumnTasksData(column) {
      if (!column || !column.id) return null;
      const cid = String(column.id);
      if (this.activeTab === 'my') return this.myTasksData[cid] || null;
      if (this.activeTab === 'viewer') return this.viewerTasksData[cid] || null;
      return this.allTasksData[cid] || null;
    },
    getColumnTotal(column) {
      const data = this.getColumnTasksData(column);
      return data ? data.total || 0 : 0;
    },
    getColumnHasMore(column) {
      const data = this.getColumnTasksData(column);
      return data ? data.hasMore || false : false;
    },
    getColumnLoading(column) {
      if (!column || !column.id) return false;
      const cid = String(column.id);
      if (this.activeTab === 'my') return this.myTasksLoading[cid] || false;
      if (this.activeTab === 'viewer') return this.viewerTasksLoading[cid] || false;
      return this.allTasksLoading[cid] || false;
    },
    resetMyTasksData() {
      const defaults = { tasks: [], page: 1, total: 0, hasMore: false };
      this.myTasksData = {};
      this.myTasksLoading = {};
      const cols = this.boardData && this.boardData.columns ? this.boardData.columns : [];
      cols.forEach(function (col) {
        const cid = String(col.id);
        this.myTasksData[cid] = { ...defaults };
        this.myTasksLoading[cid] = false;
      }.bind(this));
    },
    async fetchTasksForAllColumns() {
      if (!this.boardId || !this.boardData || !this.boardData.columns) return;
      this.boardTasksFetchBatchId = (this.boardTasksFetchBatchId || 0) + 1;
      const batchId = this.boardTasksFetchBatchId;
      const searchTrim = (this.boardTaskSearch || '').trim();
      const cols = this.boardData.columns;
      if (this.activeTab === 'my' && this.currentUserId) {
        for (let i = 0; i < cols.length; i++) {
          await this.fetchMyTasks(cols[i].id, 1, true, batchId, searchTrim, true);
          if (batchId !== this.boardTasksFetchBatchId) return;
        }
      } else if (this.activeTab === 'viewer' && this.currentUserId) {
        for (let i = 0; i < cols.length; i++) {
          await this.fetchViewerTasks(cols[i].id, 1, true, batchId, searchTrim, true);
          if (batchId !== this.boardTasksFetchBatchId) return;
        }
      } else {
        for (let i = 0; i < cols.length; i++) {
          await this.fetchAllTasks(cols[i].id, 1, true, batchId, searchTrim, true);
          if (batchId !== this.boardTasksFetchBatchId) return;
        }
      }
      if (batchId === this.boardTasksFetchBatchId) {
        this.updateLocalColumns(this.boardData);
      }
    },
    async fetchMyTasks(columnId, page, replace, batchId, searchTrim, skipLocalUpdate) {
      if (!this.boardId || !this.currentUserId) return;
      const cid = String(columnId);
      if (!this.myTasksData[cid]) this.myTasksData[cid] = { tasks: [], page: 1, total: 0, hasMore: false };
      this.myTasksLoading[cid] = true;
      try {
        const searchVar = searchTrim && searchTrim.length > 0 ? searchTrim : null;
        const result = await this.$apollo.query({
          query: gql`
            query GetMyTasksByColumn($board_id: ID!, $column_id: ID!, $assignee_id: ID, $search: String, $first: Int, $page: Int) {
              boardTasksByColumn(board_id: $board_id, column_id: $column_id, assignee_id: $assignee_id, search: $search, first: $first, page: $page) {
                data {
                  id token title description priority status due_date completed_at
                  estimated_minutes estimated_time_type actual_seconds_spent time_progress_percent checklist_progress_percent is_timer_running
                  column { id name }
                  assignees { id name email }
                  viewers { id name email }
                  created_by { id name }
                }
                paginatorInfo { total currentPage hasMorePages perPage }
              }
            }
          `,
          variables: {
            board_id: this.boardId,
            column_id: columnId,
            assignee_id: this.currentUserId,
            search: searchVar,
            first: 10,
            page: page || 1,
          },
          fetchPolicy: 'network-only',
        });
        const payload = result.data && result.data.boardTasksByColumn;
        if (!payload) return;
        if (batchId !== this.boardTasksFetchBatchId) return;
        const tasks = payload.data || [];
        const paginator = payload.paginatorInfo || {};
        if (replace) {
          this.myTasksData[cid].tasks = tasks;
        } else {
          this.myTasksData[cid].tasks = this.myTasksData[cid].tasks.concat(tasks);
        }
        this.myTasksData[cid].page = paginator.currentPage || page;
        this.myTasksData[cid].total = paginator.total || 0;
        this.myTasksData[cid].hasMore = paginator.hasMorePages || false;
        if (!skipLocalUpdate) {
          this.updateLocalColumns(this.boardData);
        }
      } catch (e) {
        console.error('Error fetching my tasks:', e);
      } finally {
        this.myTasksLoading[cid] = false;
      }
    },
    async loadMoreMyTasks(columnId) {
      const cid = String(columnId);
      const data = this.myTasksData[cid];
      if (!data) return;
      const nextPage = (data.page || 1) + 1;
      const batchId = this.boardTasksFetchBatchId;
      const searchTrim = (this.boardTaskSearch || '').trim();
      await this.fetchMyTasks(columnId, nextPage, false, batchId, searchTrim);
    },
    resetAllTasksData() {
      const defaults = { tasks: [], page: 1, total: 0, hasMore: false };
      this.allTasksData = {};
      this.allTasksLoading = {};
      const cols = this.boardData && this.boardData.columns ? this.boardData.columns : [];
      cols.forEach(function (col) {
        const cid = String(col.id);
        this.allTasksData[cid] = { ...defaults };
        this.allTasksLoading[cid] = false;
      }.bind(this));
    },
    resetViewerTasksData() {
      const defaults = { tasks: [], page: 1, total: 0, hasMore: false };
      this.viewerTasksData = {};
      this.viewerTasksLoading = {};
      const cols = this.boardData && this.boardData.columns ? this.boardData.columns : [];
      cols.forEach(function (col) {
        const cid = String(col.id);
        this.viewerTasksData[cid] = { ...defaults };
        this.viewerTasksLoading[cid] = false;
      }.bind(this));
    },
    async fetchViewerTasks(columnId, page, replace, batchId, searchTrim, skipLocalUpdate) {
      if (!this.boardId || !this.currentUserId) return;
      const cid = String(columnId);
      if (!this.viewerTasksData[cid]) this.viewerTasksData[cid] = { tasks: [], page: 1, total: 0, hasMore: false };
      this.viewerTasksLoading[cid] = true;
      try {
        const searchVar = searchTrim && searchTrim.length > 0 ? searchTrim : null;
        const result = await this.$apollo.query({
          query: gql`
            query GetViewerBoardTasksByColumn($board_id: ID!, $column_id: ID!, $viewer_id: ID!, $search: String, $first: Int, $page: Int) {
              viewerBoardTasksByColumn(board_id: $board_id, column_id: $column_id, viewer_id: $viewer_id, search: $search, first: $first, page: $page) {
                data {
                  id token title description priority status due_date completed_at
                  estimated_minutes estimated_time_type actual_seconds_spent time_progress_percent checklist_progress_percent is_timer_running
                  column { id name }
                  assignees { id name email }
                  viewers { id name email }
                  created_by { id name }
                }
                paginatorInfo { total currentPage hasMorePages perPage }
              }
            }
          `,
          variables: {
            board_id: this.boardId,
            column_id: columnId,
            viewer_id: this.currentUserId,
            search: searchVar,
            first: 10,
            page: page || 1,
          },
          fetchPolicy: 'network-only',
        });
        const payload = result.data && result.data.viewerBoardTasksByColumn;
        if (!payload) return;
        if (batchId !== this.boardTasksFetchBatchId) return;
        const tasks = payload.data || [];
        const paginator = payload.paginatorInfo || {};
        if (replace) {
          this.viewerTasksData[cid].tasks = tasks;
        } else {
          this.viewerTasksData[cid].tasks = this.viewerTasksData[cid].tasks.concat(tasks);
        }
        this.viewerTasksData[cid].page = paginator.currentPage || page;
        this.viewerTasksData[cid].total = paginator.total || 0;
        this.viewerTasksData[cid].hasMore = paginator.hasMorePages || false;
        if (!skipLocalUpdate) {
          this.updateLocalColumns(this.boardData);
        }
      } catch (e) {
        console.error('Error fetching viewer tasks:', e);
      } finally {
        this.viewerTasksLoading[cid] = false;
      }
    },
    async loadMoreViewerTasks(columnId) {
      const cid = String(columnId);
      const data = this.viewerTasksData[cid];
      if (!data) return;
      const nextPage = (data.page || 1) + 1;
      const batchId = this.boardTasksFetchBatchId;
      const searchTrim = (this.boardTaskSearch || '').trim();
      await this.fetchViewerTasks(columnId, nextPage, false, batchId, searchTrim);
    },
    async fetchAllTasks(columnId, page, replace, batchId, searchTrim, skipLocalUpdate) {
      if (!this.boardId) return;
      const cid = String(columnId);
      if (!this.allTasksData[cid]) this.allTasksData[cid] = { tasks: [], page: 1, total: 0, hasMore: false };
      this.allTasksLoading[cid] = true;
      try {
        const searchVar = searchTrim && searchTrim.length > 0 ? searchTrim : null;
        const result = await this.$apollo.query({
          query: gql`
            query GetBoardTasksByColumn($board_id: ID!, $column_id: ID!, $search: String, $first: Int, $page: Int) {
              boardTasksByColumn(board_id: $board_id, column_id: $column_id, search: $search, first: $first, page: $page) {
                data {
                  id token title description priority status due_date completed_at
                  estimated_minutes estimated_time_type actual_seconds_spent time_progress_percent checklist_progress_percent is_timer_running
                  column { id name }
                  assignees { id name email }
                  viewers { id name email }
                  created_by { id name }
                }
                paginatorInfo { total currentPage hasMorePages perPage }
              }
            }
          `,
          variables: {
            board_id: this.boardId,
            column_id: columnId,
            search: searchVar,
            first: 10,
            page: page || 1,
          },
          fetchPolicy: 'network-only',
        });
        const payload = result.data && result.data.boardTasksByColumn;
        if (!payload) return;
        if (batchId !== this.boardTasksFetchBatchId) return;
        const tasks = payload.data || [];
        const paginator = payload.paginatorInfo || {};
        if (replace) {
          this.allTasksData[cid].tasks = tasks;
        } else {
          this.allTasksData[cid].tasks = this.allTasksData[cid].tasks.concat(tasks);
        }
        this.allTasksData[cid].page = paginator.currentPage || page;
        this.allTasksData[cid].total = paginator.total || 0;
        this.allTasksData[cid].hasMore = paginator.hasMorePages || false;
        if (!skipLocalUpdate) {
          this.updateLocalColumns(this.boardData);
        }
      } catch (e) {
        console.error('Error fetching board tasks:', e);
      } finally {
        this.allTasksLoading[cid] = false;
      }
    },
    async loadMoreColumnTasks(column) {
      if (!column || !column.id) return;
      const columnId = column.id;
      if (this.activeTab === 'my') {
        await this.loadMoreMyTasks(columnId);
      } else if (this.activeTab === 'viewer') {
        await this.loadMoreViewerTasks(columnId);
      } else {
        const cid = String(columnId);
        const data = this.allTasksData[cid];
        if (!data) return;
        const nextPage = (data.page || 1) + 1;
        const batchId = this.boardTasksFetchBatchId;
        const searchTrim = (this.boardTaskSearch || '').trim();
        await this.fetchAllTasks(columnId, nextPage, false, batchId, searchTrim);
      }
    },
    updateLocalColumns(boardData) {
      if (!boardData || !boardData.columns) {
        this.localColumns = [];
        return;
      }

      // Deep clone columns so draggable can mutate safely
      const cloned = JSON.parse(JSON.stringify(boardData.columns));
      cloned.sort((a, b) => (a.order || 0) - (b.order || 0));

      if (this.activeTab === 'my') {
        cloned.forEach(col => {
          const cid = String(col.id);
          const data = this.myTasksData[cid];
          col.tasks = data && data.tasks ? [...data.tasks] : [];
        });
      } else if (this.activeTab === 'viewer') {
        cloned.forEach(col => {
          const cid = String(col.id);
          const data = this.viewerTasksData[cid];
          col.tasks = data && data.tasks ? [...data.tasks] : [];
        });
      } else {
        cloned.forEach(col => {
          const cid = String(col.id);
          const data = this.allTasksData[cid];
          col.tasks = data && data.tasks ? [...data.tasks] : [];
        });
      }

      this.localColumns = cloned;
    },
    handleBarClick(event, chartContext, config) {
      const index = config.dataPointIndex;
      const member = this.boardMembers && this.boardMembers[index];
      if (member) {
        this.openMemberTasksModal(member);
      }
    },
  },
};
</script>

<style scoped>
.task-board-page {
  min-height: calc(100vh - 80px);
}

.task-ghost {
  opacity: 0.5;
}

.task-drag {
  opacity: 0.9;
}

/* Column UX: cap height + allow vertical scrolling inside each column */
.kanban-column {
  max-height: calc(100vh - 240px);
}

/* Important for flex children to scroll properly */
.kanban-column-body {
  overflow-y: auto;
  padding-right: 4px;
}

.kanban-column-body::-webkit-scrollbar {
  width: 6px;
}

.kanban-column-body::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.6);
  /* gray-400 */
  border-radius: 9999px;
}

.kanban-column-body::-webkit-scrollbar-track {
  background: transparent;
}

.kanban-empty-dropzone {
  min-height: 240px;
  height: 100%;
}

.text-8px {
  font-size: 8px;
}

/* Member card stat pills */
.stat-pill {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 2.5rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.5rem;
  background: #f8fafc;
  transition: all 0.2s ease;
}

.stat-pill .stat-label {
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: #64748b;
  line-height: 1.2;
}

.stat-pill .stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 0.125rem;
}

.attachment-preview-content {
  min-height: 400px;
}

.attachment-preview-img {
  max-height: 75vh;
}

.attachment-preview-iframe {
  min-height: 75vh;
}

.stat-pill.stat-total .stat-value {
  color: #1e293b;
}

.stat-pill.stat-total {
  background: #f1f5f9;
}

.stat-pill.stat-todo .stat-value {
  color: #475569;
}

.stat-pill.stat-todo {
  background: #f1f5f9;
}

.stat-pill.stat-todo.stat-empty .stat-value {
  color: #94a3b8;
}

.stat-pill.stat-progress .stat-value {
  color: #2563eb;
}

.stat-pill.stat-progress {
  background: #eff6ff;
}

.stat-pill.stat-progress.stat-empty .stat-value {
  color: #93c5fd;
}

.stat-pill.stat-paused .stat-value {
  color: #d97706;
}

.stat-pill.stat-paused {
  background: #fffbeb;
}

.stat-pill.stat-paused.stat-empty .stat-value {
  color: #fcd34d;
}

.stat-pill.stat-review .stat-value {
  color: #7c3aed;
}

.stat-pill.stat-review {
  background: #f5f3ff;
}

.stat-pill.stat-review.stat-empty .stat-value {
  color: #c4b5fd;
}

.stat-pill.stat-done .stat-value {
  color: #059669;
}

.stat-pill.stat-done {
  background: #ecfdf5;
}

.stat-pill.stat-done.stat-empty .stat-value {
  color: #6ee7b7;
}

/* Statistics tiles - clean professional style */
.stat-tile {
  transition: box-shadow 0.2s ease;
}

.stat-tile:hover {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.stat-tile-total {
  border-left: 3px solid #3b82f6;
}

.stat-tile-todo {
  border-left: 3px solid #64748b;
}

.stat-tile-progress {
  border-left: 3px solid #2563eb;
}

.stat-tile-paused {
  border-left: 3px solid #d97706;
}

.stat-tile-review {
  border-left: 3px solid #7c3aed;
}

.stat-tile-done {
  border-left: 3px solid #059669;
}

/* Task card description - max 3 lines with ellipsis */
.task-card-description {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Progress bar animation - diagonal stripes + shimmer */
@keyframes task-progress-flow {
  0% {
    background-position: 0 0, -100% 0;
  }

  100% {
    background-position: 24px 0, 200% 0;
  }
}

.task-progress-bar-fill {
  background-image:
    repeating-linear-gradient(105deg,
      transparent 0,
      transparent 4px,
      rgba(255, 255, 255, 0.2) 4px,
      rgba(255, 255, 255, 0.2) 8px),
    linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 45%,
      rgba(255, 255, 255, 0.5) 50%,
      rgba(255, 255, 255, 0.35) 55%,
      transparent 100%);
  background-size: 24px 100%, 200% 100%;
  background-position: 0 0, -100% 0;
}

.task-progress-bar-fill--animated {
  animation: task-progress-flow 1.2s linear infinite;
}

.task-progress-bar-fill--static {
  background-image: none !important;
  background-size: unset !important;
  background-position: unset !important;
}

.quick-add-assignees-multiselect :deep(.multiselect__content-wrapper) {
  z-index: 100;
}

.board-view-toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
  border-radius: 0.5rem;
  transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.board-view-toggle-icon {
  width: 1rem;
  height: 1rem;
  font-size: 1rem;
}

.board-view-toggle-btn:hover {
  color: #334155;
  background: rgba(255, 255, 255, 0.6);
}

.board-view-toggle-btn-active {
  color: #1d4ed8;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.1);
}

.board-list-view {
  scroll-margin-top: 5rem;
}

.board-list-table-card {
  min-height: 200px;
}

.board-list-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.board-list-th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.board-list-th-num {
  width: 3rem;
  text-align: center;
}

.board-list-th-action {
  width: 2.5rem;
}

.board-list-row {
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.board-list-row:hover {
  background-color: #f8fafc;
}

.board-list-row:nth-child(even) {
  background-color: #fafbfc;
}

.board-list-row:nth-child(even):hover {
  background-color: #f1f5f9;
}

.board-list-td {
  padding: 0.75rem 1rem;
  font-size: 0.8125rem;
  color: #0f172a;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
}

.board-list-td-num {
  text-align: center;
  font-weight: 700;
  color: #64748b;
  font-size: 0.75rem;
}

.board-list-td-muted {
  color: #475569;
  max-width: 10rem;
}

.board-list-td-action {
  text-align: center;
  vertical-align: middle;
}

.board-list-title {
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}

.board-list-desc {
  margin: 0.25rem 0 0 0;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 280px;
}

.board-list-bucket {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
  color: #334155;
}

.board-list-bucket-dot {
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  flex-shrink: 0;
}

.board-list-priority {
  display: inline-block;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: capitalize;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  border: 1px solid;
  background: #fff;
}

.board-list-status {
  font-size: 0.6875rem;
}

.board-list-due-late {
  color: #dc2626;
  font-weight: 600;
}

.board-list-pagination {
  max-width: 100%;
}

.board-list-table-wrap--loading {
  opacity: 0.55;
  pointer-events: none;
  transition: opacity 0.15s ease;
}
.archive-explorer {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f9fafb;
  overflow: visible;
}

.archive-address-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
  padding: 10px 14px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  font-size: 13px;
  line-height: 1.4;
}

.archive-address-root,
.archive-address-crumb {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  color: #374151;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.archive-address-root:hover,
.archive-address-crumb:hover {
  background: #eff6ff;
  color: #1d4ed8;
}

.archive-address-sep {
  color: #9ca3af;
  padding: 0 2px;
  user-select: none;
}

.archive-address-current {
  padding: 4px 8px;
  color: #1e3a8a;
  font-weight: 600;
}

.archive-status,
.archive-empty {
  padding: 48px 24px;
  text-align: center;
}

.archive-empty-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  color: #d1d5db;
}

.archive-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  gap: 4px;
  padding: 16px;
  min-height: 180px;
  position: relative;
}

.archive-marquee {
  position: fixed;
  z-index: 30;
  border: 1px solid rgba(37, 99, 235, 0.7);
  background: rgba(59, 130, 246, 0.18);
  pointer-events: none;
}

@media (min-width: 640px) {
  .archive-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 8px;
    padding: 20px;
  }
}

.archive-grid-item {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 8px 10px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: default;
  user-select: none;
  -webkit-touch-callout: none;
  transition: background-color 0.12s ease, border-color 0.12s ease, box-shadow 0.12s ease;
}

.archive-grid-item:hover {
  background: rgba(59, 130, 246, 0.08);
  border-color: rgba(59, 130, 246, 0.2);
}

.archive-grid-item-selected {
  background: rgba(59, 130, 246, 0.14);
  border-color: rgba(37, 99, 235, 0.45);
  box-shadow: 0 0 0 1px rgba(37, 99, 235, 0.15);
}

.archive-grid-item-cut {
  opacity: 0.55;
}

.archive-item-kebab-menu {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  color: #4b5563;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease, background-color 0.12s ease, color 0.12s ease;
}

.archive-grid-item:hover .archive-item-kebab-menu,
.archive-grid-item-selected .archive-item-kebab-menu,
.archive-item-kebab-menu:focus,
.archive-item-kebab-menu-active {
  opacity: 1;
  pointer-events: auto;
}

.archive-item-kebab-menu:hover,
.archive-item-kebab-menu:focus {
  background: #f3f4f6;
  color: #111827;
  outline: none;
}

.archive-kebab-icon {
  width: 12px;
  height: 12px;
}

@media (min-width: 640px) {
  .archive-item-kebab-menu {
    display: none;
  }
}

.archive-item-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 64px;
  margin-bottom: 8px;
}

.archive-folder-icon {
  width: 56px;
  height: auto;
  max-height: 52px;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
}

.archive-file-icon {
  position: relative;
  width: 48px;
  height: 56px;
  color: #6b7280;
}

.archive-file-icon-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08));
}

.archive-file-ext {
  position: absolute;
  left: 50%;
  bottom: 14px;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  color: #ffffff;
  padding: 2px 4px;
  border-radius: 2px;
  max-width: 36px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.archive-file-pdf {
  color: #dc2626;
}

.archive-file-pdf .archive-file-ext {
  background: #dc2626;
}

.archive-file-doc {
  color: #2563eb;
}

.archive-file-doc .archive-file-ext {
  background: #2563eb;
}

.archive-file-sheet {
  color: #16a34a;
}

.archive-file-sheet .archive-file-ext {
  background: #16a34a;
}

.archive-file-ppt {
  color: #ea580c;
}

.archive-file-ppt .archive-file-ext {
  background: #ea580c;
}

.archive-file-image {
  color: #7c3aed;
}

.archive-file-image .archive-file-ext {
  background: #7c3aed;
}

.archive-file-txt {
  color: #4b5563;
}

.archive-file-txt .archive-file-ext {
  background: #4b5563;
}

.archive-file-default {
  color: #6b7280;
}

.archive-file-default .archive-file-ext {
  background: #6b7280;
}

.archive-item-label {
  width: 100%;
  margin: 0;
  padding: 0 2px;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 500;
  color: #1f2937;
  text-align: center;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.archive-item-label-wrap {
  width: 100%;
}

.archive-item-label-input {
  width: 100%;
  font-size: 12px;
  line-height: 1.35;
  font-weight: 500;
  color: #111827;
  text-align: center;
  padding: 2px 4px;
  border-radius: 4px;
  border: 1px solid #93c5fd;
  background: #eff6ff;
  outline: none;
}

.archive-meta-tooltip {
  position: absolute;
  left: 50%;
  top: calc(100% + 8px);
  transform: translateX(-50%);
  width: 220px;
  max-width: 90vw;
  padding: 10px 12px;
  border-radius: 8px;
  background: #111827;
  color: #f9fafb;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  z-index: 20;
}

.archive-meta-tooltip::after {
  content: '';
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-width: 6px;
  border-style: solid;
  border-color: transparent transparent #111827 transparent;
}

.archive-grid-item:hover .archive-meta-tooltip {
  opacity: 1;
  visibility: visible;
}

.archive-meta-title {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: #ffffff;
  word-break: break-word;
}

.archive-meta-rows {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.archive-meta-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  line-height: 1.35;
}

.archive-meta-label {
  flex-shrink: 0;
  color: #9ca3af;
}

.archive-meta-value {
  text-align: right;
  color: #f3f4f6;
  word-break: break-word;
}

.archive-context-menu {
  position: fixed;
  z-index: 50;
  min-width: 180px;
  padding: 6px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.archive-context-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #111827;
  font-size: 13px;
  line-height: 1.2;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.archive-context-menu-item:hover {
  background: #f3f4f6;
}

.archive-context-menu-item-danger {
  color: #dc2626;
}

.archive-context-menu-item-danger:hover {
  background: #fef2f2;
}

.archive-context-menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.archive-context-menu-icon-svg {
  width: 16px;
  height: 16px;
}

.archive-context-menu-label {
  flex: 1;
}
</style>
